import type { CourseId } from './courses'
import type { ProgramPartnerId } from './programPartners'

export type ProgramId =
  | 'yayasan-peneraju-2025'
  | 'kyouth-2025'
  | 'kydp-2024'
  | 'kydp-2024-b'
  | 'moses-2023'
  | 'kydp-2023'
  | 'google-career-2023'
  | 'cap-2023'
  | 'ive-train-2023'
  | 'mystep-2022'
  | 'dtfs'
  | 'google-career-2025'

export type ProgramCourse = {
  id: string
  title?: string
  category?: string[]
  description?: string
  requirements?: string[]
  recommended?: string[]
  outcomes?: string[]
  skills?: string[]
  jobs?: string[]
  link?: string
  disablePage?: boolean
  accreditation?: string[]
  provider?: string
  badge?: string
}

export type ProgramTrack = {
  title: string
  category?: string[]
  description: string
  requirements?: string[]
  recommended?: string[]
  outcomes?: string[]
  skills?: string[]
  jobs?: string[]
  link?: string
  accreditation?: string[]
  provider?: string
  providers?: string[]
  comingSoon?: boolean
  badge?: string
}

export type AppCountryId = 'my' | 'hk' | 're-global'

export type Program = {
  id: ProgramId
  title: string
  heroTitle?: string
  shortTitle: string
  applicationOpen?: boolean
  showAtDashboard?: boolean
  testRelease?: string
  applicationCitizens?: string[]
  partner: ProgramPartnerId
  since?: string
  externalLink?: {
    link: string
    linkTarget: '_blank' | '_self'
  }
  tagline: string
  subtext: string
  descriptions?: string[]
  tracks?: {
    title: string
    courses: CourseId[]
    description?: string
    link?: string
  }[]
  lmsLink?: string
  sections?: {
    hero: {
      image: string
      description: string
      badge?: string
    }
    overview: {
      title: string
      description?: string
      image?: string
      logos?: string[]
    }
    employers?: {
      title: string
      description?: string
      employers: string[]
    }
    cards?: {
      title: string
      description?: string
      blue?: boolean
      cards: {
        title?: string
        description?: string
        image?: string
        icon?: string
        size: 'half' | 'one-third' | 'two-third' | 'full' | 'third'
        color?: string
        dark?: boolean
        style?: string
      }[]
    }
    grid?: {
      column: 'two' | 'three' | 'four'
      title?: string
      description?: string
      cards: {
        image?: string
        title?: string
        description?: string
      }[]
    }
    progress?: {
      title: string
      description?: string
      progress: {
        title: string
        description?: string
        image?: string
        arrow?: string
      }[]
    }
    statistics?: {
      image?: string
      style?: string
      dark?: boolean
      separate?: boolean
      statistics: {
        title: string
        value: string
        icon: 'grad' | 'thumbs-up' | 'book' | 'briefcase' | 'person' | 'building'
        color?: string
        image?: string
        style?: string
      }[]
    }
    timeline?: {
      title: string
      description?: string
      timeline: {
        title?: string
        time?: string
        description?: string
        icon:
          | 'briefcase'
          | 'chat'
          | 'cv'
          | 'pen'
          | 'mail'
          | 'apply'
          | 'cert'
          | 'thumbs-up'
          | 'money'
          | 'book'
        color?: string
      }[]
      docLink?: string
    }
    testimony?: {
      title: string
      description?: string
      students: string[]
    }
    faq?: {
      title: string
      description?: string
      categories?: string[]
      faqs: {
        question: string
        answer: string
        category?: number
      }[]
    }
    requirements?: {
      title: string
      description?: string
      requirements: string[]
      image?: string
    }
    courses?: {
      title: string
      description?: string
      tabStyle?: boolean
      categories?: {
        title: string
        image?: string
        description?: string
        requirements?: string[]
        accreditation?: string[]
        jobs?: string[]
      }[]
      courses?: ProgramCourse[]
      tracks?: ProgramTrack[]
    }
    prompt: {
      include: boolean
      title?: string
      description?: string
      image?: string
      style?: string
      light?: boolean
      center?: boolean
    }
  }
}

const tlColor = {
  blue: '#3FA2FF',
  lightBlue: '#D4E8FA',
  green: '#6FD5D2',
  lightGreen: '#D0EFEE',
  yellow: '#F5EC83',
  purple: '#D47DFF',
  lightPurple: '#EEE2F9'
}

const googleColor = {
  blue: '#3C7AEA',
  green: '#2EA94F',
  yellow: '#FDBD00',
  red: '#ED422D'
}

const topStudents = ['pp', 'lwh', 'nhr', 'gje', 'nit', 'keak', 'es']

const kydp25 = {
  courseReqsBeginner: [
    'Academic: Degree or higher',
    'CGPA: 3.0 or higher',
    'English: Equivalent/higher than IELTS 7/MUET 4',
    'No technology background required'
  ],
  courseReqsInt: [
    'Academic: Degree or higher',
    'CGPA: 3.0 or higher',
    'English: Equivalent/higher than IELTS 8/MUET 5',
    'Background: IT/STEM preferred, but not required'
  ]
}

export const programTracks = [
  {
    title: 'Software Engineering'
  },
  {
    title: 'Data Analysis'
  },
  {
    title: 'Fintech'
  },
  {
    title: 'Digital Marketing'
  },
  {
    title: 'Technology Management'
  },
  {
    title: 'Cybersecurity'
  }
]

export const programs: Program[] = [
  {
    id: 'yayasan-peneraju-2025',
    title: 'Yayasan Peneraju Technology Certification Scheme',
    heroTitle: 'Yayasan Peneraju<br/>Technology Certification<br/>Financing Scheme',
    shortTitle: 'Yayasan Peneraju 2025',
    applicationOpen: true,
    showAtDashboard: false,
    partner: 'yayasan-peneraju',
    tagline: 'Your Skills, Certified. Your Dreams, Funded.',
    since: '2025',
    subtext:
      'Fast-track your tech career with Yayasan Peneraju — <u>100% financing</u> for professional certifications in AI, cloud computing, and more.',
    sections: {
      hero: {
        image: '/images/database/programmes/yp-2025/hero.jpg',
        badge: '/images/database/programmes/yp-2025/badge.svg',
        description: `Fast-track your tech career with Yayasan Peneraju — <u>100% financing</u> for professional certifications in AI, cloud computing, and more.`
      },
      overview: {
        title: 'Your Skills, Certified. <br/>Your Dreams, <u>Funded.</u>',
        description: `In partnership with Yayasan Peneraju, we aim to empower the next generation of talents, offering access to industry-grade cloud certification programmes in AWS & Google Cloud. 
        <br/><br/>
        With Yayasan Peneraju's commitment to providing financial aid, we will deliver training through a hybrid model (self-paced online training + virtual-instructor led training), ensuring you build the right skillset to contribute to Malaysia's tech workforce.
        <br/><br/>
        Together, preparing you for the future of technology.
        `,
        image: '/images/database/programmes/yp-2025/overview.jpg',
        logos: [
          '/images/database/employers/employer-tl.png',
          '/images/database/employers/employer-yayasan-peneraju.png',
          '/images/database/employers/employer-google.png',
          '/images/database/employers/employer-aws.png'
        ]
      },
      employers: {
        title: `Our alumni have landed opportunities at <u>world-leading companies</u> - now it's your turn!`,
        employers: [
          'airasia',
          'aia',
          'agoda',
          'accenture',
          'capbay',
          'cimb',
          'cyberport',
          'deloitte',
          'daikin',
          'dell',
          'fedex',
          'fave',
          'google',
          'gitlab',
          'godaddy',
          'grab',
          'hkte',
          'kpmg',
          'intel',
          'keysight',
          'maybank',
          'nielsen',
          'pwc',
          'lenovo',
          'tencent',
          'tvb',
          'uob',
          'sime-darby',
          'unilever'
        ]
      },
      courses: {
        title: `Choose your <u>course</u>`,
        tabStyle: true,
        courses: [
          {
            id: 'aws-csa-a',
            title: 'AWS Certified Solutions Architect (Associate)',
            description:
              'Learn to design cost-effective, high-performance solutions on AWS. This certification is ideal for those with cloud or IT experience, validating your ability to architect secure, scalable cloud systems without requiring deep coding knowledge.',
            accreditation: ['/images/database/employers/employer-aws.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Design cost-optimised and scalable cloud architectures.`,
              `Apply AWS best practices for performance and security.`,
              `Support business needs with resilient cloud solutions.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=aws-csa-a`,
            recommended: [`Beginners between 0 to 3 years' experience`]
          },
          {
            id: 'aws-csa-p',
            title: 'AWS Certified Solutions Architect (Professional)',
            description:
              'Master advanced cloud architecture with AWS. This certification validates your ability to design complex, scalable solutions, optimise security and cost, and automate cloud operations for large-scale enterprise needs.',
            accreditation: ['/images/database/employers/employer-aws.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Architect complex, secure, and scalable AWS solutions.`,
              `Optimise cloud performance, cost, and security.`,
              `Automate and streamline cloud infrastructure operations.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=aws-csa-p`,
            recommended: [`Advanced learners with more than 5 years' experience`]
          },
          {
            id: 'gc-a-ce',
            title: 'Google Cloud Associate Cloud Engineer Certification',
            description:
              'Learn to deploy, secure, and manage applications on Google Cloud. This certification validates your ability to maintain cloud solutions, monitor operations, and ensure reliable performance across projects.',
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Set up, deploy, and configure cloud solutions.`,
              `Monitor and maintain cloud operations.`,
              `Manage access, security, and scaling.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=gc-a-ce`,
            recommended: [`Beginners between 0 to 3 years' experience`]
          },
          {
            id: 'gc-p-a',
            title: 'Google Cloud Professional Cloud Engineer Certification',
            description:
              'Master the skills to design, secure, and optimise cloud architecture on Google Cloud. This certification validates your expertise in building scalable, reliable, and efficient cloud solutions for complex business needs.',
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Architect secure and scalable cloud solutions.`,
              `Optimise cloud infrastructure and operations.`,
              `Lead cloud transformation initiatives.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=gc-p-a`,
            recommended: [`Advanced learners with more than 5 years' experience`]
          },
          {
            id: 'gc-p-se',
            title: 'Google Cloud Professional Security Engineer Certification',
            description:
              'Master the skills to design, develop, and manage secure solutions on Google Cloud. This certification validates your ability to protect data, configure network security, enforce compliance, and defend cloud environments against threats.',
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Configure secure access and network boundaries.`,
              `Protect data and manage threat monitoring.`,
              `Support regulatory compliance and automate security operations.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=gc-p-se`,
            recommended: [`Advanced learners with more than 5 years' experience`]
          },
          {
            id: 'aws-ml-a',
            title: 'AWS Certified Machine Learning Engineer (Associate)',
            description:
              'Build and deploy machine learning solutions with AWS. This certification validates your skills in implementing and operationalising ML workloads, boosting your credibility for high-demand machine learning roles.',
            accreditation: ['/images/database/employers/employer-aws.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Develop and deploy ML models on AWS.`,
              `Operationalise machine learning workflows at scale.`,
              `Advance your career in machine learning roles.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=aws-ml-a`,
            recommended: [`Beginners between 0 to 3 years' experience`]
          },
          {
            id: 'aws-ml-s',
            title: 'AWS Certified Machine Learning Engineer (Specialty)',
            description:
              'Validate your expertise in building and deploying machine learning solutions on AWS. This certification proves your ability to architect ML workloads, optimise model training, and implement production-ready systems following AWS best practices.',
            accreditation: ['/images/database/employers/employer-aws.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Architect and deploy ML and deep learning solutions on AWS.`,
              `Optimise model training and tuning.`,
              `Implement scalable, production-ready ML systems.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=aws-ml-s`,
            recommended: [`Advanced learners with more than 5 years' experience`]
          },
          {
            id: 'gc-p-de',
            title: 'Google Cloud Professional Data Engineer Certification',
            description:
              'Learn to design, build, and manage secure data processing systems. This certification validates your ability to make data usable, support business needs, and maintain scalable, efficient data workflows.',
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Design and deploy data processing systems.`,
              `Ingest, store, and prepare data for analysis.`,
              `Automate and secure data workflows.`
            ],
            link: `/programmes/register/yayasan-peneraju-2025?course=gc-p-de`,
            recommended: [`Advanced learners with more than 5 years' experience`]
          }
        ]
      },
      grid: {
        column: 'three',
        title: `What sets <u>TalentLabs</u> apart`,
        cards: [
          {
            title: `Expert Guidance`,
            description: `You'll have a team supporting you — educators to sharpen your skills, programme managers to keep you on track, and career coaches to get you hired.`,
            image: '/images/database/programmes/yp-2025/grid-guidance.jpg'
          },
          {
            title: `A Global Network`,
            description: `Build global connections and join a thriving ecosystem of students, industry leaders, and hiring partners spanning Malaysia, the UK, and Hong Kong.`,
            image: '/images/database/programmes/yp-2025/grid-global.jpg'
          },
          {
            title: `Career Acceleration`,
            description: `Build a resume, Linkedin, and Github that stand out. Get hands-on interview prep, one-on-one career coaching, and a job-hunting strategy tailored just for you.`,
            image: '/images/database/programmes/yp-2025/grid-career.jpg'
          }
        ]
      },
      requirements: {
        title: `Get ready to rise. Check your <u>eligibility</u>`,
        image: `/images/database/programmes/yp-2025/requirement.jpg`,
        requirements: [
          `Malaysian Bumiputera citizen, aged 16 years old and above`,
          `Not an active recipient of other Yayasan Peneraju funding`,
          `A strong passion for tech and the drive to achieve professional certification`
        ]
      },
      testimony: {
        title: 'Over 2,000+ <u>success stories</u> and counting',
        students: topStudents
      },
      prompt: {
        include: true,
        title: `What are you waiting for?`,
        image: '/images/database/programmes/yp-2025/prompt.jpg',
        light: false
      },
      timeline: {
        title: 'How it <u>works</u>',
        timeline: [
          {
            title: 'Select your course',
            description: `<a href='#programme-courses'>Explore our exclusive courses</a>, accredited by Google & AWS, and select the right certification that aligns with your professional development.`,
            icon: 'book'
          },
          {
            title: `Complete application`,
            description: `<a href='/programmes/register/yayasan-peneraju-2025' target='_blank'>Start your application</a>, register an account with us if you don't have one yet, and start filling out the application details.`,
            icon: 'apply'
          },
          {
            title: `Pre-screening assessment`,
            description: `You will receive an email invitation to complete a short assessment, to ensure you meet the eligibility criteria for the course selected.`,
            icon: 'pen'
          },
          {
            title: `You're accepted!`,
            description: `TalentLabs will issue an offer letter and invoice to you - which you will use later for Yayasan Peneraju's financing application process. The process from application to acceptance will take around 5 working days.`,
            icon: 'mail'
          },
          {
            title: `Get Peneraju financing aid`,
            description: `Through <a href='https://peneraju.org/' target='_blank'>peneraju.org</a>, you will apply for financing application to get your certification fully funded! To know more, check out the <a href='/programmes/yayasan-peneraju-2025#programme-faq'>FAQ section</a> under the "<b>General</b>" category.`,
            icon: 'money'
          },
          {
            title: `Your course is funded!`,
            description: `Process your payment with the financial aid received from Yayasan Peneraju, with proof of payment required by TalentLabs. The proof of payment process can take up to 3 working days.`,
            icon: 'thumbs-up'
          },
          {
            title: `Let's get you certified!`,
            description: `And you're set! Join us on our intakes, complete the training programme and get certified!`,
            icon: 'cert'
          }
        ]
      },
      faq: {
        title: `Frequently asked <u>questions</u>`,
        categories: ['General', 'Programme Details', 'Programme Structure'],
        faqs: [
          {
            category: 0,
            question: `What is the Yayasan Peneraju Approved Learning & Training Institution (ALTI) Technology Certification Scheme?`,
            answer: `It is a nation-wide talent development programme in collaboration with the Ministry of Economy and Yayasan Peneraju, where TalentLabs will provide skills training in preparation for cloud certifications, financed by Yayasan Peneraju.`
          },
          {
            question: `How much does this programme cost?`,
            answer: `This programme is fully financed by Yayasan Peneraju; as part of the process, you are required to submit a financing application on <a href="https://peneraju.org/" target="_blank">peneraju.org</a>. Don't forget to provide the necessary documents during the application, such as your NRIC, birth certificates and bank statements.`,
            category: 0
          },
          {
            question: `Am I eligible to take part in this programme?`,
            answer: `You will be eligible to take part in the programme if you meet the criteria below:
                <ul>
                <li>Eligible for Bumiputera status</li>
                <li>Malaysian citizen</li>
                <li>Aged 16 years and above</li>
                <li>Obtained a valid offer letter or proof of registration or certification of attendance from the Approved Learning & Training Institution (ALTI)</li>
                <li>Not an active recipient of other Yayasan Peneraju funding</li>
                <li>Have an active individual bank account</li>
                </ul>
                <br/>
                Additionally, to ensure successful completion, we recommend that you have:
                <ul>
                <li>Keen interest in cloud computing technologies</li>
                <li>Strong command in English</li>
                <li>Commitment to completion of all self-paced modules and virtual sessions</li>
                </ul>`,
            category: 0
          },
          {
            question: `What is the selection process like?`,
            answer: `Our selection process involves application reviews and a pre-screening assessment; these are to ensure you maintain eligibility and meet the basic requirements for these courses.`,
            category: 0
          },
          {
            question: `What is the structure of the programme?`,
            answer: `The programme spans 3 months, involving self-paced modules designed to develop your technical skills, and virtual sessions to enhance the learning experience; at the end of the programme, you are required to pass your examination for certification.`,
            category: 0
          },
          {
            question: `Will I receive a certificate after completing the programme?`,
            answer: `Upon completing your chosen course, you will be given an examination voucher (to be completed externally); thus you are expected to pass your examination to receive an official certificate from the awarding body (AWS or Google Cloud).`,
            category: 0
          },
          {
            question: `How can I apply for the Yayasan Peneraju Approved Learning & Training Institution (ALTI) Technology Certification Scheme?`,
            answer: `You may send in your online application by completing this online application process <a href="https://peneraju.org/" target="_blank">here</a>. The approval process can take up to 21 days. Don't forget to provide the necessary documents during the application, such as your NRIC, birth certificates and bank statements.`,
            category: 0
          },
          {
            question: `What are the next steps after I have successfully completed the online application on TalentLabs?`,
            answer: `You may continue to <a href="https://peneraju.org/" target="_blank">peneraju.org</a> to submit a financing application for the course of choice, pending approval from Yayasan Peneraju. You will be required to fill in the programme name, provider, start date, and amount of financial aid requested, as stated in TalentLabs' offer letter. Additionally, you will need to provide these documents:
                <ul>
                <li>NRIC (front & back)</li>
                <li>Birth certificate</li>
                <li>Bank statement</li>
                <li>SPM slip</li>
                <li>Academic transcript</li>
                </ul>
                <br/>
                The approval process might take up to 21 working days.`,
            category: 0
          },
          {
            question: `How long can I expect the process to take?`,
            answer: `Successful applicants will hear back from TalentLabs within 5 working days; financing approval from Yayasan Peneraju is expected within 21 working days.`,
            category: 0
          },
          {
            question: `Will I be notified if my online application is unsuccessful?`,
            answer: `While we strive to inform all applicants individually, the volume of submissions may make this challenging. However, we value every application, and your interest is appreciated.`,
            category: 0
          },
          {
            question: `What happens if I do not receive any updates on my financing application?`,
            answer: `You are encouraged to regularly check for your application status on <a href="https://peneraju.org/" target="_blank">peneraju.org</a>; if your status remains unchanged, please direct your financing-related questions to <a href="mailto:askme@peneraju.org" target="_blank">askme@peneraju.org</a>.`,
            category: 0
          },
          {
            question: `What are the courses available?`,
            answer: `Participants can choose one (1) course from the following:
              <ul>
              <li>AWS Certified Solutions Architect (Associate) - recommended for beginners</li>
              <li>AWS Certified Solutions Architect (Professional)</li>
              <li>AWS Certified Machine Learning (Associate)</li>
              <li>AWS Certified Machine Learning (Specialty)</li>
              <li>Google Cloud Associate Cloud Engineer - recommended for beginners</li>
              <li>Google Cloud Professional Cloud Architect</li>
              <li>Google Cloud Professional Data Engineer</li>
              <li>Google Cloud Professional Cloud Security Engineer</li>
              </ul>`,
            category: 1
          },
          {
            question: `When do the courses commence?`,
            answer: `Classes are organised into 2 intakes in 2025, each with distinct start dates:
              <ul>
              <li>Intake 1 | 1st August</li>
              <li>Intake 2 | 1st November</li>
              </ul>
              <br/>
              Please note that class dates may be subject to change or cancellation by TalentLabs.`,
            category: 1
          },
          {
            question: `Is it possible to switch cohorts before the class commences?`,
            answer: `Cohort changes will be considered based on availability of allocated seats, and financing approval from Yayasan Peneraju. Please communicate your requests by replying to the email you receive from TalentLabs, and communicating your changes to <a href="mailto:askme@peneraju.org" target="_blank">askme@peneraju.org</a>.`,
            category: 1
          },
          {
            question: `Where will the courses be held and what preparation is needed before taking the course?`,
            answer: `All courses will be conducted virtually (online). Participants will need access to a computer/laptop and a reliable internet connection.`,
            category: 1
          },
          {
            question: `What is the structure of these courses? Are they fully self-paced or instructor-led?`,
            answer: `Our courses are all delivered with a mix of both self-paced online training modules, and virtual instructor-led training sessions; attendance and completion is mandatory.`,
            category: 2
          },
          {
            question: `When should I complete all the self-paced online training modules by?`,
            answer: `Our courses are designed to be completed within the stipulated timeframe of 12 weeks, with sufficient time to prepare yourself for certification examinations.`,
            category: 2
          },
          {
            question: `When will the virtual instructor-led training sessions be held?`,
            answer: `For the benefit of students who are full-time employees, all virtual instructor-led training sessions will be held after working hours.`,
            category: 2
          },
          {
            question: `What is the process of the examination?`,
            answer: `At present, upon full attendance and completion of course, TalentLabs will provide you an examination voucher, valid for 6 months upon issue. It is therefore your responsibility to claim and use this voucher to sit for the examination with an external proctor. Please note that any additional retakes are at your own expense.`,
            category: 2
          },
          {
            question: `Will participants be required to share information about their certification, after completing the course and passing the examination?`,
            answer: `As part of our programme evaluation and reporting processes, participants are required to share necessary information regarding their certification. This may include details such as your grade and date of completion. Your cooperation in providing this information is essential for us to track programme outcomes accurately and ensure ongoing programme improvement.`,
            category: 2
          }
        ]
      }
    }
  },
  {
    id: 'google-career-2025',
    title: 'Google Career Certificates 2025',
    shortTitle: 'GCC 2025',
    applicationOpen: true,
    showAtDashboard: true,
    partner: 'google',
    tagline: 'From Learning to Earning',
    since: '2023',
    subtext: `The Google Career Certificate program is an online training program that offers professional certificates in fast-growing, high-demand technology fields.`,
    sections: {
      hero: {
        image: '/images/database/programmes/gcc-25/hero.jpg',
        description: `From Learning to Earning<br/>Google Career Certificates are your shortcut to real world skills and professional success! Get job-ready skills to open new career opportunities!`
      },
      overview: {
        title: 'Master your <u>job-ready skills,</u> with Google Career Certificates',
        description: `
        Master the skills you need to thrive in today's job market with Google Career Certificates. Designed by industry experts, these flexible, online courses provide in-demand, practical knowledge to help you become job-ready in a matter of months. Whether you're launching your first career, transitioning into a new field, or leveling up your current role, Google Career Certificates are your shortcut to gaining real-world skills that employers value. 
        <br/><br/>
        With no degree required, you can boost your resume, grow your expertise, and unlock exciting new opportunities in fields like IT, data analytics, project management, and more. Your career success starts here!`,
        image: '/images/database/programmes/gcc-25/overview.jpg',
        logos: [
          '/images/database/employers/employer-google.png',
          '/images/database/employers/employer-tl.png',
          '/images/database/employers/employer-hrm.png',
          '/images/database/employers/employer-cityu.png',
          '/images/database/employers/employer-hkust.png'
        ]
      },
      employers: {
        title:
          'Our alumni have successfully secured placements and job offers at <u>top-tier companies</u>',
        employers: [
          'airasia',
          'aia',
          'agoda',
          'accenture',
          'capbay',
          'cimb',
          'cyberport',
          'deloitte',
          'daikin',
          'dell',
          'fedex',
          'fave',
          'google',
          'gitlab',
          'godaddy',
          'grab',
          'hkte',
          'kpmg',
          'intel',
          'keysight',
          'maybank',
          'nielsen',
          'pwc',
          'lenovo',
          'tencent',
          'tvb',
          'uob',
          'sime-darby',
          'unilever'
        ]
      },
      cards: {
        title: `What we <u>offer</u>`,
        cards: [
          {
            title: 'Professional Networking Opportunities',
            description: `Develop your career and discover great opportunities by being part of TalentLabs extensive network of students, mentors and hiring partners based across the globe, from Canada to Hong Kong to Malaysia, and a lot more.`,
            size: 'two-third',
            icon: '/images/database/programmes/gcc-25/card-icon-handshake.svg',
            image: '/images/database/programmes/gcc-25/card-0.jpg',
            dark: true
          },
          {
            title: 'Career Management & Employability Support',
            description: `We help you navigate the complex job application process while matching you with the most suitable career path in technology. From CV reviews to technical interview preparations, we've got you covered.`,
            size: 'one-third',
            icon: '/images/database/programmes/gcc-25/card-icon-briefcase-2.svg',
            color: googleColor.yellow
          }
        ]
      },
      statistics: {
        dark: true,
        statistics: [
          {
            title: 'Past students',
            value: '2,000+',
            icon: 'person',
            color: googleColor.blue
          },
          {
            title: 'Placements',
            value: '500+',
            icon: 'briefcase',
            color: googleColor.green
          },
          {
            title: 'Pass rate',
            value: '95%',
            icon: 'grad',
            color: googleColor.yellow
          }
        ]
      },
      timeline: {
        title: 'How it <u>works</u>',
        timeline: [
          {
            title: 'Application',
            time: '1 Feb - 1 Oct 2025',
            description: `Apply to the programme <a target="_blank" href="/programmes/register/google-career-2025">here</a>, or follow the designatied application process from your distribution partner.`,
            icon: 'apply',
            color: googleColor.blue
          },
          {
            title: `Approval`,
            time: '10 Feb - 10 Oct 2025',
            description:
              'You will receive your approval email in 2 weeks from application, depending on the distribution partner internal process.',
            icon: 'thumbs-up',
            color: googleColor.green
          },
          {
            title: `Learning`,
            time: '10 Feb - 31 Dec 2025',
            description: `Enroll in your interested course and study the course at your own pace. Attend community events organized by Google and TalentLabs.`,
            icon: 'pen',
            color: googleColor.yellow
          },
          {
            title: `Certification`,
            description: `A digital Professional Career Certificates will be issued to you once you have completed the course and passed all the requirements. You can also enroll into more coureses in the programme.`,
            icon: 'cert',
            color: googleColor.red
          }
        ]
      },
      testimony: {
        title: 'Over <u>2,000+ graduates</u> since 2023',
        description: '87% have no prior background in tech',
        students: topStudents
      },
      requirements: {
        title: 'Who we are <u>looking for</u>',
        image: `/images/database/programmes/gcc-25/requirement.jpg`,
        requirements: [
          'Open to all students, including those with no prior background in tech',
          'Students who are looking to upskill to equip themselves with job-ready skills'
        ]
      },
      faq: {
        title: 'FAQ',
        categories: ['General', 'Online Training'],
        faqs: [
          {
            category: 0,
            question: 'What is this programme about?',
            answer: `Google Career Certficates provide job seekers with access to in-demand skills through job training without the need for a college degree or prior experience.`
          },
          {
            category: 0,
            question: `How much does it cost?`,
            answer: `This programme is completely <b>FREE</b>, and you will also recieve a certificate from Google upon completion.`
          },
          {
            category: 1,
            question: `How long does the course take?`,
            answer: `The course duration averagely at 100 hours, however some of our course is as short as 10 hours, and some of the advanced course will take up to 200 hours.`
          },
          {
            category: 1,
            question: `Can I take as long as I want to learn the course?`,
            answer: `Yes you can, however this free programme's expiry date is at 31st Dec 2025 so it's advisable to complete the course before the date, in order to get your certificate.`
          }
        ]
      },
      courses: {
        title: `Your pathways into <u>real-world careers</u>`,
        description: 'Explore our courses',
        categories: [
          {
            title: 'Data Science' // 0
          },
          {
            title: 'IT Support' // 1
          },
          {
            title: 'Cybersecurity' // 2
          },
          {
            title: 'Business Management' // 3
          },
          {
            title: 'Digital Marketing' // 4
          },
          {
            title: 'Project Management' // 5
          },
          {
            title: 'UI/UX Design' // 6
          }
        ],
        courses: [
          {
            id: 'gcc-aie',
            category: ['0'],
            description: `Google AI Essentials is an under 10 hours self-paced course designed to help people across roles and industries get essential AI skills to boost their productivity, zero experience required. The course is taught by AI experts at Google who are working to make the technology helpful for everyone. They'll do more than teach about AI — they'll show how to actually use it in the real world.`,
            skills: [
              'Using generative AI tools',
              'Writing effective prompts',
              'Using AI responsibly'
            ],
            disablePage: true,
            link: `/programmes/register/google-career-2025`
            // jobs: [
            //   'Content creator',
            //   'Digital marketing & media analyst',
            //   'Administrative assistant',
            //   'Small business owner'
            // ]
          },
          {
            id: 'gcc-its',
            category: ['1'],
            description: `Certificate graduates understand concepts core to all IT support jobs, and know how to assemble a computer, write effective support documentation, route paths and subnets, manage device software, and more.`,
            skills: [
              `Customer support`,
              'Troubleshooting',
              'Network',
              `Security`,
              `System administration`,
              `Operating system`
            ],
            disablePage: true,
            jobs: [
              'Database administrator',
              'IT support specialist',
              'Help desk technician',
              'System analyst',
              'IT technician',
              'Systems analyst'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-ita',
            category: ['1'],
            description: `IT Automation with Python Certificate is designed to help individuals with a background in IT Support advance to technical paths like Systems Administrators.`,
            skills: ['Python', 'System administration', 'Github', 'Cloud computing', 'Debugging'],
            disablePage: true,
            jobs: [
              'Advanced IT support specialist',
              'Junior systems analyst',
              'Junior systems administrator',
              'Network specialist'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-da',
            category: ['0'],
            description:
              'Certificate graduates have a working knowledge of essential data analysis platforms — including spreadsheets, SQL, Tableau and R programming — and when to use them in the data life cycle. Graduates also understand how to translate a business question into a data analysis exercise and to distill findings into actionable takeaways. <b>#1 most popular certificate.</b>',
            skills: [
              'Finding patterns',
              'Coding SQL & R',
              'Data Visualization',
              'Analyzing Data',
              'Planning',
              'Sharing'
            ],
            disablePage: true,
            jobs: [
              'Associate data analyst',
              'Data technician',
              'Business analyst',
              'Junior data analyst',
              'Risk analyst',
              'Operations analyst'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-pm',
            category: ['5'],
            description:
              'Certificate graduates know how to run traditional and Agile projects and programs from initiation to completion. They possess a toolbox of templates and artifacts to manage an array of scenarios and project elements, like schedules, budgets, and product requirements.',
            skills: [
              'Scheduling & Project management software',
              'Team management',
              'Negotiating & communication',
              'Budget management',
              'Customer service',
              'Presentation'
            ],
            disablePage: true,
            jobs: [
              'Project manager',
              'Scrum master',
              'Project coordinator',
              'Operations manager',
              'Program manager',
              'Associate solutions consultant',
              'Supply chain coordinator'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-ud',
            category: ['5', '6'],
            description:
              'Certificate graduates know how to follow the UX design process, understand the basics of UX research, and can work with Figma and Adobe XD — two of the most in-demand design tools by employers.',
            skills: [
              'User research',
              'Wireframe',
              'Prototype',
              'Test designs',
              'Usability studies',
              'Create a portfolio'
            ],
            disablePage: true,
            jobs: [
              'UX designer',
              'UI designer',
              'Product designer',
              'Visual designer',
              'UX consultant',
              'UX strategist'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-dme',
            category: ['4'],
            description:
              'Certificate graduates learn the fundamentals of digital marketing and e-commerce to help businesses grow their online presence and sales.',
            skills: [
              'Engaging customers',
              'Content marketing',
              'Email marketing',
              'Marketing analytics',
              'Selling online',
              'Creating a portfolio'
            ],
            disablePage: true,
            jobs: [
              'Marketing coordinator',
              'Marketing assistant / associate / specialist',
              'Search engine optimization specialist / analyst',
              'Paid search specialist',
              'Media coordinator',
              'Media planner',
              'Email marketing specialist',
              'E-commerce associate',
              'E-commerce analyst'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-ada',
            category: ['0'],
            description:
              'Certificate graduates learn how to use machine learning, predictive modeling, and experimental design to collect and analyze large amounts of data.',
            skills: [
              'Data science',
              'Machine learning',
              'Coding Python',
              'Predictive modelling',
              'Exploratory data analytics',
              'Statistical analysis'
            ],
            disablePage: true,
            jobs: [
              'Senior data analyst',
              'Junior data scientist',
              'Data science analyst',
              'Data analytics scientist'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-bi',
            category: ['0', '3'],
            description:
              'Certificate graduates learn how to create processes and information channels that transform data into actionable insights to inform business decisions.',
            skills: [
              'Business intelligence',
              'Extract transform load data',
              'Visualisations',
              'Tableau',
              'Dashboard & reporting',
              'Business analysis'
            ],
            disablePage: true,
            jobs: [
              'Business intelligence analyst',
              'Business intelligence engineer',
              'Business intelligence developer',
              'Business data analyst'
            ],
            link: `/programmes/register/google-career-2025`
          },
          {
            id: 'gcc-cs',
            category: ['2'],
            description:
              'The Google Cybersecurity Certificate teaches learners how to identify common cybersecurity risks, threats, and vulnerabilities, as well as the techniques to mitigate them.',
            skills: [
              'Python',
              'Linux',
              'SQL',
              'SIEM tools',
              'IDS',
              'Network Security',
              'Information Security',
              'NIST Cybersecurity network'
            ],
            disablePage: true,
            jobs: [
              'Cybersecurity Analyst',
              'Security Analyst',
              'Security Operations Center (SOC) Analyst',
              'Information Security Analyst',
              'IT Security Analyst',
              'Cyber Defense Analyst'
            ],
            link: `/programmes/register/google-career-2025`
          }
        ]
      },
      prompt: {
        include: true,
        title: 'Are you ready to fast-track your way to real-world skills?'
      }
    }
  },
  {
    id: 'kydp-2024-b',
    title: 'K-Youth Development Programme Track B',
    shortTitle: 'K-Youth 2024 (B)',
    applicationOpen: true,
    showAtDashboard: true,
    partner: 'khazanah',
    // externalLink: {
    //   link: 'https://k-youth-umk.talentlabs.org',
    //   linkTarget: '_blank'
    // },
    tagline: 'Fuel your tech dreams with TalentLabs',
    descriptions: [
      'Exclusive to 2024 3rd-year University Malaysia Kelantan students',
      `Hands-on training in collaboration with top-tier companies`
    ],
    subtext:
      'Exclusive to 2024 3rd-year University Malaysia Kelantan students, TalentLabs presents an all-inclusive technology upskilling program designed to empower you through hands-on training in collaboration with top-tier companies.',
    sections: {
      hero: {
        image: '/images/database/programmes/kydp-b-24/hero.jpg',
        description: `<b>Fuel your tech dreams with TalentLabs</b>
            <br/>Exclusive to UMK students, TalentLabs presents an all-inclusive technology upskilling program designed to empower you through hands-on training in collaboration with top-tier companies.`
      },
      overview: {
        title: `Embark on your <u>tech journey</u>`,
        description: `In partnership with Khazanah Nasional Berhad, K-Youth is a youth employability initiative which aims to develop Malaysia's future talents. The programme will equip youth with commercially useful skills and experience to enhance their employability.
        <br/><br/>
        Khazanah and TalentLabs remains committed to supporting the nation's youth employability, aligned with our Advancing Malaysia strategy and aspirations of Malaysia MADANI.`,
        image: `/images/database/programmes/kydp-b-24/overview.jpg`,
        logos: [
          '/images/database/employers/employer-tl.png',
          '/images/database/employers/employer-khazanah.png'
        ]
      },
      timeline: {
        title: 'Forging your <u>path in tech</u>',
        timeline: [
          {
            title: 'Apply now',
            description: `<ul>
                <li>Fill in the application form.</li>
                <li>Choose your preferred career path.</li>
                <li>Upload relevant documents such as Malaysian NRIC, CV and Academic Transcript.</li>
                </ul>`,
            icon: 'apply'
          },
          {
            title: `You're accepted!`,
            description: `<ul>
            <li>Congratulations! Now all's that left is your employment paperwork.</li>
            <li>This set of agreement details important policies as well as your start and programme graduation dates. Make sure to read it carefully and ensure all the information you've entered are accurate.</li>
            </ul>`,
            icon: 'mail'
          },
          {
            title: 'Foundation certificate training',
            time: 'Year 1',
            description: `Self-paced and online-based technical + soft skills development. You can opt for either TalentLabs' Data Analvtics or Digital Marketing Courses. Explore career paths <a>here.</a>`,
            icon: 'cert'
          },
          {
            title: 'Career guidance',
            time: 'Year 1',
            description: `Ongoing career guidance and emplovability support throughout the 2-year programme helping you with CV and cover letter reviews, application strategy, mock interviews personal branding and more`,
            icon: 'cv'
          },
          {
            title: 'Internship facilitation',
            time: 'Year 1',
            description: `TalentLabs will facilitate and connect you with potential employers according to your interests and relevant course of study.`,
            icon: 'chat'
          },
          {
            title: 'Work placement support',
            time: 'Year 2',
            description: `TalentLabs will aid your job placement opportunities upon our graduation and completion of training`,
            icon: 'briefcase'
          },
          {
            title: 'Additional support + services + rewards',
            time: 'Throughout Year 1-2',
            description: `<ul>
            <li>Industry workshops and seminars</li>
            <li>Access to TalentLabs alumni network and exclusive career events</li>
            <li>Mentorships and career counselling</li>
            <li>Course completion stipend</li>
            </ul>`,
            icon: 'thumbs-up'
          }
        ]
      },
      courses: {
        title: `Choose your <u>career path</u>`,
        courses: [
          {
            id: 'fcda',
            description: `Data Analytics is the application of modern data analytics tools, data visualisation & optimisation to solve real-world business problems and help you understand the role of evidence-based data in decision making.`,
            jobs: [
              'Data Analyst',
              'Data Scientist',
              'Business Analyst',
              'Management Associate',
              'Operations Executive'
            ],
            requirements: [`Active UMK student as of 2024`, `Current CGPA: 3.0 or higher`],
            link: `/programmes/register/kydp-2024-b?pathway=Foundation Certificate`
          },

          {
            id: 'fcdm',
            description: `Digital Marketing entails everything that happens on a digital platform, whether it's on a website, social media, or through email. You will learn to manage marketing campaigns by leveraging data on customers, products and interactions.`,
            jobs: ['Digital Marketer', 'Content Strategist', 'Brand Manager', 'Sales Executive'],
            requirements: [`Active UMK student as of 2024`, `Current CGPA: 3.0 or higher`],
            link: `/programmes/register/kydp-2024-b?pathway=Foundation Certificate`
          }
        ]
      },
      requirements: {
        title: `Who are we <u>looking for?</u>`,
        image: `/images/database/programmes/kydp-b-24/requirement.jpg`,
        requirements: [
          `Outstanding interpersonal skills, results-driven, agile, positive attitude towards learning and "can-do attitude"`,
          `Solid computational thinking, problem-solving and analytical skills, with a passion for technology roles and industry.`,
          `Active UMK students as of 2024, with good academic performance, strong command in English and a keen interest in technology.`
        ]
      },
      faq: {
        title: `Frequently asked <u>questions</u>`,
        categories: ['General', 'Online training', 'Internship', 'Career placement'],
        faqs: [
          {
            category: 0,
            question: `What is the K-Youth Development Programme (K-Youth Programme)?`,
            answer: `It is a structured talent development programme in collaboration with the Ministry of Finance and Khazanah Nasional Berhad, where TalentLabs will provide extensive skills training and career placement support.`
          },
          {
            category: 0,
            question: `What is K-Youth Development Programme Track B: University Pipeline?`,
            answer: `It is a scholarship programme for University Malaysia Kelantan (UMK) students to receive Foundation-level technical training, soft skills development, career guidance, internship opportunities and career placement support.`
          },
          {
            category: 0,
            question: `How much does this programme cost?`,
            answer: `Not only is it FREE, but you will receive a cash stipend upon completion of the programme`
          },
          {
            category: 0,
            question: `Am I eligible to take part in this programme?`,
            answer: `You will be eligible to take part in the programme if you meet the criteria below:
                <ul>
                <li>Malaysian citizen, below 30 years of age</li>
                <li>Active student from University Malaysia Kelantan</li>
                <li>Currently unemployed</li>
                <li>Good English proficiency</li>
                </ul>`
          },
          {
            category: 0,
            question: `What is the selection process like?`,
            answer: `Once you submit your application, we will review it based on our set criteria for the program and reach out to you if you are shortlisted.`
          },
          {
            category: 0,
            question: `What is the structure of the programme?`,
            answer: `The programme is designed to take up to 18 months where students will have the opportunity to pursue one of the two courses in the technical business pathway. They will also have the opportunity to complete an internship according to their interests and relevant course of study. TalentLabs will facilitate career placement opportunities upon graduation and completion of training. In addition, our network of industry experts, mentors and instructors, will provide personalised coaching and support throughout the programme.`
          },
          {
            category: 0,
            question: `Will I receive a certificate after completing the programme?`,
            answer: `Certainly! Upon meeting the course completion criteria, you will be awarded a certificate of completion at the end of the programme. Many of our alumni leverage this certificate to showcase their acquired skills to potential employers and within their LinkedIn network.`
          },
          {
            category: 0,
            question: `How can I apply for the K-Youth Development Programme?`,
            answer: `You can send in your application by completing this online application process <a>here.</a>
                <br/><br/>
                Limited slots are available so apply today and we look forward to reviewing your application and welcoming you to the programme!`
          },
          {
            category: 0,
            question: `When will I be informed of my application results?`,
            answer: `Successful applicants will hear back around 2-3 weeks after submitting their applications.`
          },
          {
            category: 0,
            question: `Will I be notified if my application is unsuccessful?`,
            answer: `While we would love to inform every applicant of their status individually, the sheer number of submissions might make that challenging.`
          },
          {
            category: 0,
            question: `Will there be a contract with TalentLabs?`,
            answer: `Yes, participants will be required to sign a scholarship contract to ensure participants complete the programme and adhere to all other codes of conduct set by TalentLabs and UMK throughout the duration of the programme.`
          },
          {
            category: 0,
            question: `Would participants be bonded to TalentLabs after completing the programme?`,
            answer: `No, participants will not be bonded to TalentLabs after completing the programme. However, participants will be invited to join TalentLabs' vast network of students, mentors and hiring partners spanning the UK, Hong Kong and Malaysia.`
          },
          {
            category: 1,
            question: `What are the courses and career tracks available?`,
            answer: `Participants of the programme can choose one of the two paths available under the technical business pathway — Foundation Certificate in Data Analytics or Foundation Certificate in Digital Marketing.`
          },
          {
            category: 1,
            question: `When do the courses commence?`,
            answer: `Our classes are organised on a rolling intake basis. TalentLabs will work with UMK to announce application periods.`
          },
          {
            category: 1,
            question: `Is it possible to switch courses before my classes commence?`,
            answer: `A cohort change can be considered based on availability. Let us know your requests by replying to the email you're contacted with.`
          },
          {
            category: 1,
            question: `Where will the courses be held and what preparation is needed before taking the course?`,
            answer: `All our courses will be conducted virtually (online). You'll need access to a computer/laptop and a reliable Internet connection.`
          },
          {
            category: 1,
            question: `How long do I have to complete the online course?`,
            answer: `The course is structured to be completed by December 2024 but the programme will officially end in December 2025.`
          },
          {
            category: 2,
            question: `When should I begin seeking an internship opportunity?`,
            answer: `We recommend starting your search for an internship during the first week of your course. This is beneficial because one of the first modules focuses on improving your CV and profile.`
          },
          {
            category: 2,
            question: `What kind of assistance can I expect in seeking an internship?`,
            answer: `We are your career support hub, offering skill enhancement through courses, personalised coaching, networking, and connections with hiring partners. Our dedicated career management team at TalentLabs ensures a smooth transition to tech, guiding you from CV crafting to mock interviews and application navigation. If securing an internship is challenging, we step in to match based on availability and needs, ensuring fairness for all.`
          },
          {
            category: 3,
            question: `What career placement opportunities are available?`,
            answer: `TalentLabs will assist you in acquiring career placement opportunities. You may also pursue direct employment at any company.`
          },
          {
            category: 3,
            question: `Will I be guaranteed a job after the programme?`,
            answer: `Our goal is to offer comprehensive assistance throughout your job search, which encompasses upskilling, personalised coaching, networking prospects and introductions to potential employers so that you can secure a career placement upon programme completion. By proactively engaging in the job search process and fulfilling your career-related obligations, we enhance your prospects of securing a position aligned with your aspirations.`
          },
          {
            category: 3,
            question: `What if I don't secure a career placement after the programme?`,
            answer: `The Career Success team remains available for additional support, including CV reviews and career coaching, for an extra 60 days after the programme concludes. While we are committed to assisting you in your job search, it's important to note that participants are expected to put their best efforts in securing employment following programme completion.`
          }
        ]
      },
      prompt: {
        include: true,
        title: 'Join the digital revolution, and start your future now'
      }
    }
  },
  {
    id: 'kyouth-2025',
    title: 'K-Youth Development Programme 2025',
    shortTitle: 'K-Youth 2025',
    applicationOpen: true,
    // testRelease: `/programmes/register/kyouth-2025?pathway=Software%20Engineering&title=Data%20Analytics&tab=0`,
    partner: 'khazanah',
    since: '2023',
    tagline: `Learn, build, launch. Your tech journey starts here.`,
    subtext: `TalentLabs' K-Youth Development Programme is your launchpad into tech—gain real-world experience, industry-recognized certification, and RM2.5k monthly pay while building your future.`,
    sections: {
      hero: {
        image: '/images/database/programmes/kydp-25/hero.jpg',
        description: `This programme is your launchpad into the tech industry! With us, you can gain real-world experience, industry-recognised certification, and RM2,500 monthly pay while building your future.`
      },
      overview: {
        title: `Your journey starts <u>here</u>`,
        description: `No experience? No problem. TalentLabs' K-Youth Development Programme is a 4-month, paid apprenticeship designed to get you job-ready. Gain real-world experience, hands-on training, and industry-recognized certifications while working with top companies. This isn't just training—it's your career launchpad.`,
        image: '/images/database/programmes/kydp-25/overview.jpg',
        logos: [
          '/images/database/employers/employer-khazanah.png',
          '/images/database/employers/employer-tl.png',
          //   '/images/database/employers/employer-silicon-school.png',
          '/images/database/employers/employer-cdg.png'
        ]
      },
      statistics: {
        image: '/images/database/programmes/kydp-25/students-banner.svg',
        style: `background-image: linear-gradient(0deg, #9FD4D2 0%, #FAFFFF 100%);
                border: 1px solid black;
                box-shadow: 2px 5px black;
                border-radius: 15px;`,
        statistics: [
          {
            title: 'Hiring partners',
            value: '100+',
            icon: 'building',
            color: tlColor.blue
          },
          {
            title: 'Graduates trained since 2021',
            value: '2000+',
            icon: 'briefcase',
            color: tlColor.purple
          },
          {
            title: `Successfully hired within 3 months`,
            value: '90%',
            icon: 'person',
            color: tlColor.blue
          }
        ]
      },
      employers: {
        title: 'Where our graduates <u>work</u>',
        employers: [
          'khazanah',
          'deloitte',
          'petronas',
          'google',
          'mbsb',
          'maybank',
          'tvb',
          'cimb',
          'pos',
          'foodpanda',
          'daikin',
          'accenture',
          'agoda',
          'keysight',
          'godaddy',
          'dell',
          'fedex',
          'sime-darby',
          'hl',
          'intel',
          'nielsen',
          'airasia',
          'lenovo',
          'fave',
          'motorola',
          'shopee',
          'sunway-digital',
          'aia',
          'pwc',
          'takaful',
          'kpj',
          'uob'
        ]
      },
      timeline: {
        title: 'How does it <u>work?</u>',
        timeline: [
          {
            title: 'Apply now!',
            description: `<ul>
                <li>Fill in the application form.</li>
                <li>Choose your preferred career path.</li>
                <li>Upload relevant documents such as Malaysian NRIC, CV and Academic Transcript.</li>
                </ul>`,
            icon: 'apply'
          },
          {
            title: 'Online Assessments & Interviews',
            description: `<ul>
                    <li>Complete a virtual interview, which provides you with the opportunity to display the qualities that your CV can't and reassure us that you've thought carefully about your career choice!</li>
                    <li>Complete both technical assessments and aptitude test that assess your ability to solve problems, analyse and use information, think critically, and learn new skills.</li>
                    </ul>`,
            icon: 'chat'
          },
          {
            title: `You're accepted!`,
            description: `<ul>
                <li>Congratulations! Now all's that left is your employment paperwork.</li>
                <li>This set of agreement details important policies as well as your start and programme graduation dates. Make sure to read it carefully and ensure all the information you've entered are accurate.</li>
                </ul>`,
            icon: 'mail'
          }
        ]
      },
      cards: {
        title: `Let's unlock your <u>perfect career fit</u>`,
        blue: true,
        cards: [
          {
            title: `Structured Learning Environment`,
            description: `A 4-month structured programme combining hands-on experience with industry-recognized certification, setting you up for success in the tech industry.`,
            size: 'one-third',
            color: tlColor.yellow,
            icon: '/images/database/programmes/kydp-25/card-icon-book.svg',
            image: '/images/database/programmes/cards/one-third-white-bg-2.svg',
            style: 'box-shadow: 0px 10px 25px rgba(160, 169, 189, 0.5);'
          },
          {
            title: `Expert Guidance`,
            description: `You'll have a team supporting you — educators to sharpen your skills, programme managers to keep you on track, and career coaches to get you hired.`,
            size: 'two-third',
            // image: '/images/database/programmes/gcc-25/card-0.jpg',
            // dark: true,
            color: 'white',
            icon: '/images/database/programmes/kydp-25/card-icon-pencil.svg',
            image: '/images/database/programmes/cards/two-third-white-bg-2.svg',
            style: 'box-shadow: 0px 10px 25px rgba(160, 169, 189, 0.3);'
          },
          {
            title: `Career Acceleration`,
            description: `Build a resume, Linkedin, and Github that stand out. Get hands-on interview prep, one-on-one career coaching, and a job-hunting strategy tailored just for you.`,
            size: 'two-third',
            image: '/images/database/programmes/cards/two-third-white-bg-1.svg',
            icon: '/images/database/programmes/kydp-25/card-icon-briefcase.svg',
            // dark: true,
            style: 'box-shadow: 0px 10px 25px rgba(160, 169, 189, 0.3);',
            color: 'white'
          },
          {
            title: `A Global Network`,
            description: `Build global connections and join a thriving ecosystem of students, industry leaders, and hiring partners spanning Malaysia, the UK, and Hong Kong.`,
            size: 'one-third',
            color: tlColor.purple,
            image: '/images/database/programmes/cards/one-third-white-bg-1.svg',
            icon: '/images/database/programmes/gcc-25/card-icon-handshake.svg',
            style: 'box-shadow: 0px 10px 25px rgba(160, 169, 189, 0.7);'
          }
        ]
      },
      requirements: {
        title: `Are you the perfect <u>K-Youth candidate?</u>`,
        image: `/images/database/programmes/kydp-25/requirement.jpg`,
        requirements: [
          `Solid computational thinking`,
          `Degree holder`,
          `Passionate in tech`,
          `Fresh graduate or unemployed`,
          `30 years old and below`
        ]
      },
      testimony: {
        title: 'Over 2,000+ <u>graduates</u> since 2021',
        students: topStudents
      },
      courses: {
        title: 'Explore our <u>courses</u>',
        categories: [
          {
            title: 'AI & Data Science', // 1
            description: `AI & Data Science explores the cutting-edge technologies transforming industries through intelligent systems and data-driven insights. In these courses, you'll dive into the fundamentals of prompt engineering, data analytics, and machine learning models, equipping you with the skills to harness AI for real-world problems.`,
            jobs: [
              `Data Analyst`,
              `Data Engineer`,
              `Machine Learning Engineer`,
              `Data Scientist`,
              `Prompt Engineer`
            ]
          },
          {
            title: 'Software Engineering', // 2
            description: `Software Engineering is the art and science of designing, developing, and testing software applications. With these courses, you'll learn the fundamentals of coding, software architecture, and problem-solving techniques, equipping you with the skills to build efficient and scalable applications.`,
            jobs: [
              `Software Engineer`,
              `Full-Stack Developer`,
              `DevOps Engineer`,
              `Systems Architect`,
              `Technical Consultant`
            ]
          },
          {
            title: 'Creative & Media', // 3
            description: `Creative & Media focuses on the innovative process of designing engaging user experiences and crafting effective digital marketing strategies. In these courses, you'll explore the principles of UX design, digital content creation, and data-driven marketing techniques to connect with audiences and drive brand success.`,
            jobs: [
              `Digital Marketer`,
              `Content Strategist`,
              `Brand Manager`,
              `UX Designer`,
              `Product Manager`
            ]
          },
          {
            title: 'Digital Transformation', // 4
            description: `Digital Transformation is the process of leveraging technologies to reshape business operations. With these courses, you'll explore the fundamentals of fintech, along with strategies in other key functions, ensuring you have the skills to transform traditional business models and ultimately enhance efficiency with technology.`,
            jobs: [
              `Investment Analyst`,
              `Financial Analyst`,
              `Sales Operations Executive`,
              `Business Development Associate`,
              `People Analyst`
            ]
          }
          //   {
          //     title: 'IC Design (coming soon)', // 5
          //     description: `IC Design is essential in powering everything from smartphones to AI, and these courses provide a hands-on foundation in digital and analog design to help you master the technology shaping our future.`,
          //     jobs: [
          //       `RTL Engineer`,
          //       `DFT Engineer`,
          //       `Design Architect`,
          //       `Custom Layout Engineer`,
          //       `Modelling Engineer`
          //     ]
          //   },
          //   {
          //     title: 'Advanced Packaging (coming soon)', // 6
          //     description: `Advanced Packaging combines multiple chips into compact, high-performance devices, and these courses explore 2.5D and 3D technologies for high packaging density and enhanced energy efficiency.`,
          //     jobs: [
          //       `FPGA Engineer`,
          //       `Packaging Engineer`,
          //       `Test Engineer`,
          //       `ASIC Engineer`,
          //       `R&D Engineer`
          //     ]
          //   }
        ],
        tracks: [
          {
            title: 'Front-end Development',
            description: `This course prepares you with the knowledge to start building responsive websites with HTML, CSS, JavaScript and React. In addition, learn how to leverage open-sourced libraries like Bootstrap UI Library and the React web application framework to build an interactive website with dynamic and complex features.`,
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            outcomes: [
              'Master complex CSS techniques for responsive and adaptive design.',
              'Deepen your understanding of JavaScript for advanced interactivity and data handling.',
              'Explore modern JavaScript frameworks like React or Vue for efficient UI development.',
              'Implement state management solutions to manage app complexity and user experiences.',
              'Learn to integrate APIs for dynamic web content and extend functionality.'
            ],
            skills: ['Figma', 'HTML', 'CSS', 'Boostrap', 'Tailwind CSS', 'Javascript', 'React'],
            category: ['1'],
            requirements: kydp25.courseReqsInt,
            link: `/programmes/register/kyouth-2025?pathway=Software%20Engineering&tab=0`,
            provider: '/images/landing/talentlabs-logo.svg'
          },
          {
            title: 'Back-end Development',
            description: `This course will enhance your skills in building robust server-side applications using modern frameworks and connecting with external services. Backend development allows you to contribute significantly to tech innovations, solving complex problems with efficient, scalable code.`,
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            outcomes: [
              'Deepen your understanding of SQL and database management for complex systems.',
              'Learn to create dynamic web applications using JavaScript frameworks like Express.js.',
              'Master Python for backend development, including using Django to build robust applications.',
              'Integrate external APIs and services with backend applications to enhance functionality.',
              'Explore advanced backend technologies and their real-world applications in creating scalable software solutions.'
            ],
            skills: ['HTML', 'CSS', 'Javascript', 'React', 'NodeJS', 'SQL', 'Python'],
            category: ['1'],
            requirements: kydp25.courseReqsInt,
            link: `/programmes/register/kyouth-2025?pathway=Software%20Engineering&tab=0`,
            provider: '/images/landing/talentlabs-logo.svg'
          },
          {
            title: 'Data Analytics',
            description: `Learn the fundamentals of Data Analytics and gain an understanding of the data ecosystem, the process and lifecycle of data analytics, career opportunities, and the different learning paths you can take to be a Data Analyst.`,
            accreditation: ['/images/database/programmes/course-accreditation/google-cloud.png'],
            outcomes: [
              'Learn essential data analysis methods and tools',
              'Master Machine Learning algorithms',
              'Create AI solutions and advanced predictive models',
              'Build a solid portfolio and learn to work in a data team'
            ],
            skills: [
              'SQL',
              'Python',
              'Matplotlib',
              'Seaborn',
              'Pandas',
              'Numpy',
              'Jupyter Notebook'
            ],
            category: ['0'],
            requirements: kydp25.courseReqsInt,
            link: `/programmes/register/kyouth-2025?pathway=Data%20Science%20%26%20AI&tab=0`,
            provider: '/images/landing/talentlabs-logo.svg'
          },
          {
            title: 'Machine Learning',
            description: `Learn the fundamentals of Machine Learning and explore the machine learning processes, and real-world applications. Discover techniques used by professional data scientists and ML experts, gaining practical knowledge and hands-on experience in applying algorithms.`,
            skills: ['Tensorflow', 'OpenAI', 'LLM Agents', 'RAG', 'Apache Spark', 'Python'],
            category: ['0'],
            link: `/programmes/register/kyouth-2025?pathway=Data%20Science%20%26%20AI&tab=0`,
            provider: '/images/landing/talentlabs-logo.svg',
            outcomes: [
              `Master deep learning architectures like CNNs, RNNs, and Transformers using TensorFlow and Keras.`,
              `Gain hands-on experience with GPT models, fine-tuning techniques, and AI applications using OpenAI API.`,
              `Build and optimise machine learning models for tasks like regression, clustering, and recommendation systems.`,
              `Work with large-scale data using Apache Spark and Python.`,
              `Apply advanced techniques like Retrieval-Augmented Generation (RAG), reinforcement learning, and Bayesian methods.`
            ],
            requirements: kydp25.courseReqsInt
          },
          {
            title: 'Prompt Engineering',
            description: `Learn to craft effective AI prompts, optimize their performance, apply them across industries, integrate them into automation, and build AI-powered applications using LLMs. `,
            outcomes: [
              'Understanding the foundations of prompt engineering',
              'Leverage prompt engineering across multiple domains',
              'Optimise and evaluate prompt performances',
              'Integrate prompt engineering into automation flows',
              'Create prompt-based applications using LLM'
            ],
            skills: ['Open AI', 'Python', 'Zapier', 'Notion', 'Jasper'],
            category: ['0'],
            requirements: kydp25.courseReqsInt,
            link: `/programmes/register/kyouth-2025?pathway=Data%20Science%20%26%20AI&tab=0`,
            providers: [
              '/images/landing/talentlabs-logo.svg',
              '/images/database/programmes/course-accreditation/learn-prompting.png'
            ]
          },
          {
            title: 'Digital Marketing',
            description: `Understand the basics of digital marketing, including strategies for social media, SEO, and email marketing to enhance online visibility and engagement.`,
            outcomes: [
              'Understand the key components of digital marketing, including SEO, PPC, and social media strategies.',
              'Learn how to develop and execute effective content marketing strategies that engage and convert.',
              'Master the use of digital marketing tools and analytics to measure and optimize campaign performance.',
              'Explore the principles of email marketing and how to create successful campaigns.',
              'Gain insights into building a strong digital brand and maintaining online presence.'
            ],
            skills: ['Google Analytics', 'Hubspot', 'SEMRush'],
            category: ['2'],
            requirements: kydp25.courseReqsBeginner,
            link: `/programmes/register/kyouth-2025?pathway=Creative%20%26%20Media&tab=0`,
            provider: '/images/landing/talentlabs-logo.svg'
          },
          {
            title: 'UX Design',
            description: `Design an impactful user experience for today's modern, digital products. Build a design portfolio which will showcase your ability to build high-quality, user-centred applications.`,
            outcomes: [
              'Understand the fundamentals of user interface (UI) and user experience (UX) design principles.',
              'Learn to conduct user research and usability testing to gather actionable insights.',
              'Master the basics of wireframing, prototyping, and iterative design processes.',
              'Explore the use of design tools like Adobe XD, Sketch, and Figma for creating engaging interfaces.',
              'Develop skills in creating responsive designs that work across different devices and platforms.'
            ],
            skills: ['Figma', 'HTML', 'CSS', 'Tailwind CSS', 'Bootstrap'],
            category: ['2'],
            requirements: kydp25.courseReqsBeginner,
            link: `/programmes/register/kyouth-2025?pathway=Creative%20%26%20Media&tab=0`,
            providers: [
              '/images/landing/talentlabs-logo.svg',
              '/images/database/programmes/course-accreditation/uxdi.png'
            ]
          },
          {
            title: 'Fintech',
            accreditation: ['/images/database/programmes/course-accreditation/acca.png'],
            description: `Designed for individuals with a passion for Fintech and Digital Banking, explore key modules like Python programming, Machine Learning, and Blockchain, thus opening doors to exciting career opportunities in the evolving field of finance.`,
            outcomes: [
              `Explore the basics of fintech and its impact on traditional banking and finance.`,
              `Understand blockchain technology and its applications in digital transactions.`,
              `Learn about the latest trends in digital payments, cryptocurrencies, and mobile banking.`,
              `Analyze the regulatory environment for fintech and how it shapes innovation.`,
              `Discover how to use big data and AI to enhance financial services.`
            ],
            skills: [`SQL`, `Tableau`, `Python`, `Web3`, `Bitcoin`, `Ethereum`],
            category: ['3'],
            requirements: kydp25.courseReqsBeginner,
            link: `/programmes/register/kyouth-2025?pathway=Digital%20Transformation&tab=0`,
            providers: [
              '/images/landing/talentlabs-logo.svg',
              '/images/database/programmes/course-accreditation/acca-ta.png'
            ]
          },
          {
            title: 'Tech Sales',
            description: `Built for individuals exploring a career in Tech Sales, gain a comprehensive understanding of technology concepts and acquire crucial skills such as prospecting, handling objections, and closing deals, to succeed in sales-focused positions.`,
            outcomes: [
              `Grasp the fundamentals of technology, from basic programming to data analytics`,
              `Understand the importance of consultative selling and the modern sales life-cycle`,
              `Explore multi-channel prospecting, from cold emailing to LinkedIn Sales Navigator`,
              `Learn to qualify leads, understanding prospective customers and willingness to convert`,
              `Incorporate AI into sales, increasing efficiency and turnaround time`
            ],
            skills: [
              `SQL`,
              `Python`,
              `Tableau`,
              `Salesforce`,
              `LinkedIn Sales`,
              `Apollo`,
              `HubSpot`
            ],
            category: ['3'],
            requirements: kydp25.courseReqsBeginner,
            link: `/programmes/register/kyouth-2025?pathway=Digital%20Transformation&tab=0`,
            providers: [
              '/images/landing/talentlabs-logo.svg',
              '/images/database/programmes/course-accreditation/aspireship.png'
            ]
          }
          //   {
          //     title: 'Digital Design',
          //     accreditation: ['/images/database/programmes/course-accreditation/semi.png'],
          //     description: `Learn the fundamentals of writing clean Verilog code, best practices for declaring blocks, ports, and parameters, the power and reusability of SystemVerilog for effective bug detection in verification, and the importance of clock tree synthesis in digital design, with a focus on applying these concepts to digital communication interfaces.`,
          //     outcomes: [
          //       `Explore the fundamentals of OOPs and layered testbench architectures`,
          //       `Master synthesizable RTL implementation strategies`,
          //       `Learn digital communication interfaces such as UART, SPI, and I2C`
          //     ],
          //     skills: [
          //       `Verilog`,
          //       `SystemVerilog`,
          //       `AMBA Protocol`,
          //       `Clock Tree Synthesis`,
          //       `DDR Memory`
          //     ],
          //     category: ['4'],
          //     requirements: kydp25.courseReqsInt,
          //     link: `/programmes/register/kyouth-2025?pathway=IC%20Design&tab=1`,
          //     provider: '/images/database/employers/employer-silicon-school.png'
          //   },
          //   {
          //     title: 'Analog Design',
          //     accreditation: ['/images/database/programmes/course-accreditation/semi.png'],
          //     description: `Learn the packaging and fabrication steps, the software used for layout design and simulation, the application of concepts to a simple CMOS inverter, SPICE for timing analysis and the RTL-to-GDS flow to enable communication between analog and digital blocks.`,
          //     outcomes: [
          //       `Learn how to convert basic analog blocks to hard-macro`,
          //       `Acquire baseline knowledge on the physics of MOSFET`,
          //       `Understand each and every mask level, through appropriate fabrication steps`
          //     ],
          //     skills: [`SPICE Simulations`, `DAC`, `Custom Layout`, `MOSFET`, `Design Rule Checking`],
          //     category: ['4'],
          //     requirements: kydp25.courseReqsInt,
          //     link: `/programmes/register/kyouth-2025?pathway=IC%20Design&tab=1`,
          //     provider: '/images/database/employers/employer-silicon-school.png'
          //   },
          //   {
          //     title: '2.5 / 3D Packaging',
          //     accreditation: ['/images/database/programmes/course-accreditation/semi.png'],
          //     description: `Learn to design and implement 2.5D and 3D packaging technologies, focusing on chip stacking, through-silicon vias (TSVs), microbumps, interposers, heat management, signal integrity, and using simulation tools for performance validation, reliability testing, and power delivery optimization.`,
          //     outcomes: [
          //       `Develop skills in chip stacking and integration using 2.5D and 3D packaging.`,
          //       `Acquire proficiency in managing thermal challenges and ensuring signal reliability`,
          //       `Learn to apply simulation tools and ensure long-term reliability in complex systems`
          //     ],
          //     skills: [`MEMS`, `Silicon Photonics`, `Failure Analysis`, `Thermal Management`],
          //     category: ['5'],
          //     requirements: kydp25.courseReqsInt,
          //     link: `/programmes/register/kyouth-2025?pathway=Advanced%20Packaging&tab=1`,
          //     provider: '/images/database/employers/employer-silicon-school.png'
          //   }
        ]
      },
      faq: {
        title: 'Frequently asked questions',
        categories: ['General', 'Online training', 'On-the-job training', 'Job placement'],
        faqs: [
          {
            category: 0,
            question: `What is the K-Youth Development Programme?`,
            answer: `It is a structured talent development programme in collaboration with the Ministry of Finance and Khazanah Nasional Berhad, where TalentLabs will provide extensive skills training and career placement support.`
          },
          {
            category: 0,
            question: `How much does this programme cost?`,
            answer: `Not only is it FREE, but you will also receive a salary of RM 2,500 per month, throughout the 4-month programme.`
          },
          {
            category: 0,
            question: `Am I eligible to take part in this programme?`,
            answer: `You will be eligible to take part in the programme if you meet the criteria below:
                <ul>
                    <li>Malaysian citizen, 30 years and below as of 2025</li>
                    <li>Minimum Bachelor's degree qualification from an IPTA, IPTS or foreign university</li>
                    <li>Currently in your 3rd year (semester 5 or 6) of your degree programme</li>
                    <li>Currently unemployed</li>
                    <li>Strong command of English</li>
                    <li>STEM background preferred but not required</li>
                </ul>`
          },
          {
            category: 0,
            question: `What is the selection process like?`,
            answer: `Our rigorous selection process involves application reviews for eligibility, virtual interviews via an AI model, and online assessments (computational and aptitude) to assess logical thinking and culture fit.`
          },
          {
            category: 0,
            question: `What is the structure of the programme?`,
            answer: `The programme spans 4 months, with <b>1-month of online learning</b> to enhance your skills and <b>3-months of on-the-job training</b> with our hiring partners.`
          },
          {
            category: 0,
            question: `When are the intakes for the K-Youth Development Programme 2025?`,
            answer: `The K-Youth Development Programme has fixed intake dates each month: 
            <ul>
                <li>2nd May 2025</li>
                <li>3rd June 2025</li>
                <li>1st July 2025</li>
                <li>1st August 2025</li>
                <li>2nd September 2025</li>
            </ul>
            <br/>
            Please note that TalentLabs reserves the right to change the intake schedule from time to time.`
          },
          {
            category: 0,
            question: `Will I receive a certificate after completing the programme?`,
            answer: `Certainly! Upon completing your chosen course, you will be awarded a certificate of completion. Many of our alumni leverage this certificate to showcase their acquired skills to potential employers.`
          },
          {
            category: 0,
            question: `Will I be provided a salary during the programme?`,
            answer: `Yes, you will receive a monthly salary of RM2,500, which includes statutory contributions (such as EPF, SOCSO, etc.). This financial support will continue throughout the programme as long as you meet the specified criteria and accomplish the weekly milestones. It's important to note that any relocation allowance WILL NOT be offered.`
          },
          {
            category: 0,
            question: `How can I apply for the K-Youth Development Programme?`,
            answer: `You can send in your application by completing this online application process <a>here.</a>`
          },
          {
            category: 0,
            question: `When will I be informed of my application results?`,
            answer: `Successful applicants will hear back around 2-3 weeks after submitting their applications.`
          },
          {
            category: 0,
            question: `Will I be notified if my application is unsuccessful?`,
            answer: `While we strive to inform all applicants individually, the volume of submissions may make this challenging.
            <br/><br/>However, we value every application, and your interest is appreciated.`
          },
          {
            category: 1,
            question: `What are the courses and career tracks available?`,
            answer: `Participants can choose one (1) course from the following tracks:
            <br/><br/>
            Software Engineering Pathway:
            <ul>
            <li>Front-end Development</li>
            <li>Back-end Development</li>
            </ul>
            <br/>
            AI & Data Science Pathway:
            <ul>
            <li>Data Analytics</li>
            <li>Machine Learning</li>
            <li>Prompt Engineering</li>
            </ul>
            <br/>
            Creative & Media Pathway:
            <ul>
            <li>Digital Marketing</li>
            <li>UX Design</li>
            </ul>
            <br/>
            Digital Transformation Pathway:
            <ul>
            <li>Fintech</li>
            <li>Tech Sales</li>
            </ul>`
            // <br/>
            // IC Design Pathway (coming soon):
            // <ul>
            // <li>Digital Design</li>
            // <li>Analog Design</li>
            // </ul>
            // <br/>
            // Advanced Packaging (coming soon):
            // <ul>
            // <li>2.5/3D Packaging</li>
            // </ul>`
          },
          //   {
          //     category: 1,
          //     question: `When do the courses commence?`,
          //     answer: `Classes are organised into five cohorts in 2024, each with distinct start dates:
          //     <ul>
          //     <li>Cohort 1 | 3rd March</li>
          //     <li>Cohort 2 | 1st April</li>
          //     <li>Cohort 3 | 2nd May</li>
          //     <li>Cohort 4 | 2nd June</li>
          //     <li>Cohort 5 | 1st July</li>
          //     <li>Cohort 6 | 4th August</li>
          //     <li>Cohort 7 | 1st September</li>
          //     </ul>
          //     <br/>Please note that class dates may be subject to change or cancellation by TalentLabs.`
          //   },
          {
            category: 1,
            question: `Is it possible to switch cohorts before the class commences?`,
            answer: `Cohort changes can be considered based on availability. Please communicate your requests by replying to the email you receive from us.`
          },
          {
            category: 1,
            question: `What if I secure a job before or during the course?`,
            answer: `Fantastic! Securing a job before or during the course is encouraged. However, completing the course within one month is necessary for graduation and fulfilling your contract. We recommend selecting a start date with your future employer after completing the one-month course. Rest assured, TalentLabs will support you as you begin your career journey.`
          },
          {
            category: 1,
            question: `Where will the courses be held and what preparation is needed before taking the course?`,
            answer: `All courses will be conducted virtually (online). Participants will need access to a computer/laptop and a reliable internet connection.`
          },
          {
            category: 2,
            question: `What is On-the-Job Training (OJT), and how does it differ from a placement?`,
            answer: `On-the-Job Training (OJT) is a 12-week paid apprenticeship where participants apply theoretical knowledge in a real workplace setting. It's a chance to connect what you learn in class with practical experience. Sometimes, OJT can lead to a full-time job offer (placement) within the same organisation, based on your performance meeting the employer's expectations and available vacancies.
            <br/><br/>TalentLabs will help you find the right OJT opportunity or company that matches your goals. We also encourage you to actively seek OJT positions to ensure a tailored fit and an enriching learning experience.`
          },
          {
            category: 2,
            question: `When should I begin seeking an OJT?`,
            answer: `We recommend starting your search for an OJT during the first week of your course. This is beneficial because one of the first modules focuses on improving your CV and profile. Our aim is for you to kick off your OJT by Week 5, so early preparation is key.`
          },
          {
            category: 2,
            question: `What kind of assistance can I expect in seeking an OJT?`,
            answer: `TalentLabs serves as your OJT support hub, offering skill enhancement through courses, personalised coaching, networking opportunities, and connections with hiring partners. Our career management team provides guidance from CV crafting to mock interviews and application navigation. If securing an OJT placement proves challenging, we intervene to ensure fairness by matching based on availability and needs.`
          },
          {
            category: 3,
            question: `What job placement opportunities are available after completing the programme?`,
            answer: `TalentLabs will assist you in acquiring career placement opportunities. You may also pursue direct employment at any company. Additionally, our hiring partners may absorb you as their full-time employees depending on your performance and available vacancies.`
          },
          {
            category: 3,
            question: `Will I be guaranteed a job after the programme?`,
            answer: `While we cannot guarantee a job, our goal is to provide comprehensive assistance throughout your job search. This includes upskilling, personalised coaching, networking opportunities, and introductions to potential employers. By actively engaging in the job search process and fulfilling your career-related obligations, you enhance your prospects of securing a position aligned with your aspirations.`
          },
          {
            category: 3,
            question: `What if I don't secure a career placement after the programme?`,
            answer: `The programme duration is 16 weeks, with the provided allowance allocated within this timeframe. If a full-time job opportunity isn't secured within these 16 weeks, the allowance won't be extended. However, our Career Success team remains available for additional support, including CV reviews and career coaching, for an extra 60 days after the programme concludes.`
          },
          {
            category: 3,
            question: `Will participants be required to share information about their final placement after completing the programme?`,
            answer: `As part of our programme evaluation and reporting processes, participants are required to share necessary information regarding their final placement. This may include details such as your offer letter and start date.
            <br/><br/>Your cooperation in providing this information is essential for us to track programme outcomes accurately and ensure ongoing programme improvement.`
          }
        ]
      },
      prompt: {
        include: true,
        title: `What are you waiting for?`,
        // image: '/images/database/programmes/kydp-25/stats-break-1.png',
        light: true
      }
    }
  },
  {
    id: 'moses-2023',
    title: 'Project Moses',
    shortTitle: 'Moses 2023',
    applicationOpen: false,
    partner: 'pokoi',
    // externalLink: {
    //   link: 'https://moses.talentlabs.org',
    //   linkTarget: '_blank'
    // },
    tagline: 'Train & Place',
    subtext:
      'The purpose of Project Moses Train & Place is to support a group of young people who are interested in improving their work skills to acquire the latest practical digital skills.',
    tracks: [
      {
        ...programTracks[1],
        courses: ['m1py'],
        description: '1 foundation course, 2 associate courses'
      }
    ],
    sections: {
      hero: {
        image: '/images/database/programmes/moses/hero.jpg',
        description:
          'Unlock your potential with Python — your first step toward a future in tech starts here.'
      },
      overview: {
        title: '<u>IT innovation</u> road free funding training',
        description: `The purpose of Project Moses Train & Place is to support a group of young people who are interested in improving their work skills to acquire the latest practical digital skills. Under the trend of digital transformation, talents from all walks of life must master certain digital knowledge and skills.
              <br/><br/>
              The first phase of the program will provide participants with basic Python programming online courses. This technology is one of the necessary skills in the software engineering and data analysis industries.`,
        logos: [
          '/images/database/employers/employer-tl.png',
          '/images/database/programmes/moses/logo-moses.png'
        ]
      },
      cards: {
        title: `What we <u>offer</u>`,
        cards: [
          {
            title: 'World-Class Digital Education',
            description:
              'Offering online training and job opportunities to help you achieve your career goals.',
            size: 'third',
            color: tlColor.green,
            icon: '/images/database/programmes/kydp-25/card-icon-book.svg'
          },
          {
            title: 'Reliable Peer Network',
            description:
              'Join our exclusive Discord community and learn alongside passionate peers.',
            size: 'third',
            icon: '/images/database/programmes/kydp-25/card-icon-chat.svg',
            image: '/images/database/programmes/kydp-25/card-1.jpg',
            dark: true
          },
          {
            title: 'Guided Interactive Learning',
            description:
              'Gain support and insights with our interactive chatbot for a seamless learning experience.',
            size: 'third',
            color: tlColor.yellow,
            icon: '/images/database/programmes/kydp-25/card-icon-pencil.svg'
          }
        ]
      },
      timeline: {
        title: `How it <u>works</u>`,
        timeline: [
          {
            title: 'Register now',
            description: `Apply to participate in Project Moses Train & Place through the <a>online registration form.</a>`,
            icon: 'apply'
          },
          {
            title: 'Take the online entrace test',
            description: `Conduct basic logic tests on students through Google Form.`,
            icon: 'pen'
          },
          {
            title: `Receive confirmation notice`,
            description: `After receiving the confirmation notice, the two-month online training journey began.`,
            icon: 'mail'
          },
          {
            title: `Join the exclusive Discord learning community`,
            description: `Click the confirmation email to join the exclusive Discord learning community. All subsequent activities and career planning information will also be posted in Discord.`,
            icon: 'chat'
          }
        ]
      },
      employers: {
        title: 'Our <u>partners</u>',
        employers: [
          'google',
          'unilever',
          'mbsb',
          'samsung',
          'tencent',
          'khazanah',
          'hl',
          'tvb',
          'maybank'
        ]
      },
      faq: {
        title: 'Frequently asked <u>questions</u>',
        faqs: [
          {
            question: `Do I need an IT background to take the course?`,
            answer: `This is a beginner-friendly course with no prerequisites. If you're a beginner, our instructors can recommend resources, readings and prep work to help you prepare for the course.`
          },
          {
            question: `Who is the target audience?`,
            answer: `Pok Oi transitional housing residents (Fanling + Kong Ha Wai Estate) and members of the public in need.`
          },
          {
            question: `Is this free?`,
            answer: `Free to eligible participants (Moses Scholars). In addition, each student is required to submit a deposit of HKD 500, which will be fully refunded after 60% of the course is completed.`
          }
        ]
      },
      courses: {
        title: 'We provide <u>comprehensive</u> Python skills training',
        courses: [
          {
            id: 'm1py',
            description: `The M1 Basic Course is a set of flexible online training courses designed and taught by TalentLabs, aiming to provide basic Python knowledge to students from different backgrounds within two months. In order to promote social progress and improve labor productivity, Project Moses cooperated with TalentLabs to provide free Python M1 basic courses for 200 students.`
          }
        ]
      },
      prompt: {
        include: true
      }
    }
  },
  {
    id: 'cap-2023',
    title: 'Career Accelerator Programme',
    shortTitle: 'CAP 2023',
    applicationOpen: false,
    partner: 'mohe',
    // externalLink: {
    //   link: 'https://cap.talentlabs.org',
    //   linkTarget: '_blank'
    // },
    tagline: 'Career Accelerator Programme',
    subtext:
      'GCAP is a collaborative initiative between the Ministry of Higher Education, TalentCorp and My Partners together with leading employers to enhance the employability of graduates.',
    tracks: [
      {
        ...programTracks[1],
        courses: ['fcda', 'caba', 'cada'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Software Engineering',
        courses: ['fcse', 'cafed', 'cabed'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Digital Marketing',
        courses: ['fcdm'],
        description: '1 foundation course'
      }
    ],
    sections: {
      hero: {
        image: '/images/database/programmes/cap/hero.jpg',
        description: `Kick-start 2023 with a fully sponsored tech education worth up to RM15,000!`
      },
      overview: {
        title: `Your Tech Career Starts <u>Here</u>`,
        description: `We strive on developing future-ready talents, like you! Our goal is to help high-caliber individuals land their dream tech career.
            <br/><br/>
            Choose your desired career path and master in-demand technical skills through our Career Accelerator Programme. Our global network of mentors and instructors come from the world's leading organisations - committed to guide you in building the right skill sets for your chosen path.`
      },
      cards: {
        title: 'Built for your <u>success</u>',
        cards: [
          {
            title: 'Learn On Demand',
            description: `We aim to equip you with the necessary digital skills & knowledge required to stand out in the modern workplace, all at your own pace.`,
            size: 'third',
            color: tlColor.purple,
            icon: '/images/database/programmes/kydp-25/card-icon-book.svg'
          },
          {
            title: 'Employability Support',
            description: `We offer personalised support and guidance on how to tackle the job market while matching you with suitable career specialisations.`,
            size: 'third',
            icon: '/images/database/programmes/gcc-25/card-icon-handshake.svg',
            image: '/images/database/programmes/kydp-25/card-0.jpg',
            dark: true
          },
          {
            title: 'Career Upgrade',
            description: `Remain relevant and competitive in the workforce through upskilling, and keep yourself updated with all the in-demand digital tech skills and trend.`,
            size: 'third',
            color: tlColor.yellow,
            icon: '/images/database/programmes/gcc-25/card-icon-briefcase-2.svg'
          }
        ]
      },
      requirements: {
        title: 'Who are we <u>looking for?</u>',
        requirements: [
          'Fresh Graduates from IPTA, IPTS or Foreign Universities with strong academic performance. STEM background is preferred but not required.',
          'Outstanding interpersonal skills, results-driven, agile, positive attitude towards learning and "can-do attitude".',
          'Strong computational thinking, problem solving and analytical skills with keen interest in technology-related roles and industry.'
        ]
      },
      timeline: {
        title: `Your <u>journey</u>`,
        timeline: [
          {
            title: 'Apply now!',
            description: `<ul>
                    <li>Choose your preferred Career Specialisation.</li>
                    <li>Fill in the application form.</li>
                    <li>Upload relevant documents such as CV and Academic Transcript.</li>
                    </ul>`,
            icon: 'apply'
          },
          {
            title: `Online assessments & interviews`,
            description: `<ul>
                    <li>Complete a technical assessment which assesses your ability to solve problems, analyze and use information, think critically, and learn new skills.</li>
                    <li>Complete a one-to-one interview, which provides you with the opportunity to display the qualities that your CV can not and reassure us that you have thought carefully about your career choices!</li>
                    </ul>`,
            icon: 'chat'
          },
          {
            title: `You're accepted!`,
            description: `<ul>
                <li>Congratulations, you are in! Welcome to your new future!</li>
                <li>Follow the instructions in our email guide, join your orientation, and start learning!</li>
                </ul>`,
            icon: 'thumbs-up'
          },
          {
            title: `Online learning & Skills development`,
            description: `<ul>
                <li>2-month skills training from expert instructors & mentors*</li>
                <li>Practical work assignments and projects to build your skills</li>
                <li>Extensive career management and employability support to help you transition into your new role</li>
                <li> * Some courses may take more than 2-months to complete</li>
                </ul>`,
            icon: 'pen'
          },
          {
            title: `You're now ready for employment!`,
            description: `<ul>
                <li>Well-performed candidates will have the opportunity to be hired and placed in our extensive network of global companies.</li>
                <li> Time to put the skills you have learned into practice and earn a monthly salary of up to RM5,000!</li>
                </ul>`,
            icon: 'briefcase'
          }
        ]
      },
      courses: {
        title: 'Choose your <u>course</u>',
        categories: [
          {
            title: 'Software Engineering'
          },
          {
            title: 'Data Science'
          },
          {
            title: 'Digital Marketing'
          }
        ],
        courses: [
          {
            category: ['0'],
            id: 'cafed',
            description: `Get the skills to build engaging, interactive user experiences on the web as a Front-End Developer. Learn everything needed to design and develop user-facing code, and discover how to bring concepts to the browser by combining essential coding principles.`
          },
          {
            category: ['0'],
            id: 'cabed',
            description: `Learn how to handle the 'behind-the scenes' functionality of web applications. Back-End Development teaches the code that connects the web to a database, manages user connections, and powers the web application itself to deliver the final product to the end user.`
          },
          {
            category: ['1'],
            id: 'cada',
            description: `Learn how to utilise the tools of modern data analytics, visualization and optimisation to generate insights and solve real-world business problems.`
          },
          {
            category: ['2'],
            id: 'fcdm',
            description: `Digital Marketing entails everything that happens on a digital platform, whether it is on a website, social media, or through email. You will learn to manage marketing campaigns by leveraging data on customers, products and interactions.`
          }
        ]
      },
      employers: {
        title: 'Our alumni have successfully secured placements at <u>top-tier companies</u>',
        employers: [
          'khazanah',
          'maybank',
          'lenovo',
          'kpmg',
          'petronas',
          'nielsen',
          'pwc',
          'bnm',
          'sunway',
          'intel',
          'pos',
          'keysight',
          'mbsb',
          'foodpanda',
          'deloitte',
          'daikin',
          'godaddy',
          'hl',
          'gamuda',
          'kwsp'
        ]
      },
      testimony: {
        title: 'Our <u>graduates</u> say it best',
        students: topStudents
      },
      faq: {
        title: 'Frequently asked <u>questions</u>',
        faqs: [
          {
            question: `What is the Career Accelerator Programme?`,
            answer: `It is a fully-funded talent development programme which offers technical skills training in Front-End Development, Back-End Development and Digital Marketing. Candidates will also receive career and employability support from TalentLabs in this programme. `
          },
          {
            question: `Is there any payment required prior to joining this programme?`,
            answer: `Absolutely none! We are sponsoring your education. All you have to do is to pass the assessments and interview. `
          },
          {
            question: `Am I eligible to take part in this programme?`,
            answer: `<ul>
                <li>Malaysian Citizen</li>
                <li>IPTA, IPTS or Foreign University Graduates 2019-2022</li>
                <li>CGPA 3.0 or higher</li>
                <li>Degree Holder</li>
                <li>STEM Background is preferred</li>
                <li>Strong Command in English</li>
                <li>Proficiency in other languages is an added advantage (i.e. Mandarin, Cantonese…etc.)</li>
                </ul>`
          },
          {
            question: `What assessments do I have to take?`,
            answer: `Front-End Development and Back-End Development applicants are required to take 3 assessments:
            <ul>
            <li>Computational Test</li>
            <li>Aptitude Test</li>
            <li>Interview</li>
            </ul>
            <br/>Digital Marketing applicants are required to take 2 assessments:
            <ul>
            <li>Aptitude Test</li>
            <li>Interview</li>
            </ul>`
          },
          {
            question: `What are the course tracks available?`,
            answer: `Participants of the programme can choose 1 from the 3 courses:
            <ul>
            <li>Digital Marketing</li>
            <li>Front-End Development</li>
            <li>Back-End Development</li>
            <li>Data Analytics</li>
            </ul>`
          },
          {
            question: `When is the programme start date?`,
            answer: `The start date for next batch of the programme is 27 April 2023.`
          },
          {
            question: `What is the duration of this programme?`,
            answer: `The duration of the programme varies according to your course. 
            <ul>
            <li>Digital Marketing - 2 months</li>
            <li>Front-End Development  - 4 months</li>
            <li> Back-End Development  - 4 months</li>
            </ul>`
          },
          {
            question: `What will happen after I complete the Career Accelerator Programme?`,
            answer: `Once you have successfully completed the programme, depending on your performance, you will have an opportunity to get employed by our network of top-tier companies and start earning above average income.`
          },
          {
            question: `How can I apply for the Career Accelerator Programme?`,
            answer: `You can start applying by choosing your career plan above.
            <br/><br/>
            Limited slots available so apply today & we look forward to reviewing your application and welcome you to the programme.`
          }
        ]
      },
      prompt: {
        include: true
      }
    }
  },
  {
    id: 'ive-train-2023',
    title: 'IVE x TalentLabs Train-To-Hire Programme',
    shortTitle: 'IVE Train 2023',
    applicationOpen: false,
    partner: 'ive',
    // externalLink: {
    //   link: 'https://ive.talentlabs.org',
    //   linkTarget: '_blank'
    // },
    tagline: 'Train-to-Hire Programme',
    subtext:
      'This programme provides exclusive accessive to hundreds of new tech job opportunities across the region.',
    tracks: [
      {
        ...programTracks[1],
        courses: ['fcda', 'caba', 'cada'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Software Engineering',
        courses: ['fcse', 'cafed', 'cabed'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Digital Marketing',
        courses: ['fcdm'],
        description: '1 foundation course'
      },
      {
        title: 'Cybersecurity',
        courses: ['fif'],
        description: '1 foundation course'
      }
    ],
    sections: {
      hero: {
        image: '/images/database/programmes/ive/hero.jpg',
        description: `<b>Get paid for a 3-month training and earn a full time position in software engineering and data analytics</b>
            <br/>Through TalentLabs, you are able to get trained on the most in-demand skills in the market, and get full-time positions in various top companies in the region through our 1:1 career coaching and apprenticeship.`
      },
      overview: {
        image: '/images/database/programmes/ive/overview.jpg',
        title: 'Kick-start you tech career at <u>world-leading companies</u>',
        description: `Learn the skills that matter in this fast-moving world. Our world-class, end-to-end tech career ecosystem is what you need to realise your full potential. We provide exclusive access to hundreds of new tech job opportunities across the region. Our hiring partners hire for proven skills, not just pedigree`,
        logos: [
          '/images/database/employers/employer-tl.png',
          '/images/database/employers/employer-ive.png'
        ]
      },
      timeline: {
        title: 'Programme <u>timeline</u>',
        timeline: [
          {
            title: 'Onboarding',
            time: 'July 5',
            description: `Screening & interview`,
            icon: 'apply'
          },
          {
            title: 'Online Training',
            time: 'Month 1',
            description: `Online practical training on Web development or Data Analytics`,
            icon: 'cert'
          },
          {
            title: 'Part-time Apprenticeship',
            time: 'Month 2-3',
            description: `Guaranteed part-time apprenticeship to build actual working experiences`,
            icon: 'cv'
          },
          {
            title: 'Placement',
            time: 'Month 4',
            description: `Full-time position offer for students before graduation`,
            icon: 'briefcase'
          }
        ]
      },
      cards: {
        title: 'Undergo an IT training with a <u>compelling package</u>',
        cards: [
          {
            title: 'Apprenticeship Allowance',
            description: `Students will receive an hourly rate of $50 for the two months apprenticeship program.`,
            size: 'one-third',
            color: tlColor.blue,
            dark: true,
            icon: '/images/database/programmes/kydp-25/card-icon-briefcase.svg'
          },
          {
            title: 'Interactive Learning Space',
            description: `Our community of students, instructors and mentors come from the world's leading universities and companies. Join a global network of highly accomplised leaders in the technology industry.`,
            size: 'two-third',
            color: tlColor.yellow,
            image: '/images/database/programmes/kydp-25/card-2.svg',
            icon: '/images/database/programmes/kydp-25/card-icon-book.svg'
          },
          {
            title: 'Expected Full-time Salary',
            description: `Upon successful completion of the program, students are expected to be placed or absorbed into a full-time position with expected monthly package of $14,000 - $16,000`,
            size: 'half',
            color: tlColor.green,
            icon: '/images/database/programmes/ive/card-icon-money.svg',
            image: '/images/database/programmes/gcc-25/card-0.jpg',
            dark: true
          },
          {
            title: 'Online Training Platform',
            description: `We provide exclusive accessive to hundreds of new tech job opportunities across the region. You will go through a 3-months structured talent development programme combining a globally recognised certificate and on-the job training with TalentLab' reputable hiring partners.`,
            size: 'half',
            color: tlColor.purple,
            icon: '/images/database/programmes/ive/card-icon-computer.svg'
          },
          {
            title: 'Zero Training Cost',
            description: `There are $0 financial costs for students and institutions participating in this program.`,
            size: 'one-third',
            color: tlColor.green,
            icon: '/images/database/programmes/ive/card-icon-thumbsup.svg'
          },
          {
            title: 'One-to-One Mentoring',
            description: `We help you navigate the complex job application process while matching you with the most suitable career path in technology. From CV reviews to technical interview preparations, we've got you covered.`,
            size: 'two-third',
            image: '/images/database/programmes/kydp-25/card-1.jpg',
            icon: '/images/database/programmes/gcc-25/card-icon-handshake.svg',
            dark: true
          }
        ]
      },
      employers: {
        title: 'Our <u>clients and partners</u>',
        employers: [
          'samsung',
          'unilever',
          'tencent',
          'google',
          'gitlab',
          'maybank',
          'khazanah',
          'aia',
          'hl',
          'mbsb',
          'tvb'
        ]
      },
      statistics: {
        dark: true,
        statistics: [
          {
            title: 'Number of students',
            value: '1,300+',
            icon: 'person'
          },
          {
            title: 'Placement rate',
            value: '89%',
            icon: 'briefcase'
          },
          {
            title: 'Hiring partners',
            value: '60+',
            icon: 'building'
          }
        ]
      },
      prompt: {
        include: true
      }
    }
  },
  {
    id: 'mystep-2022',
    title: 'MySTEP',
    shortTitle: 'MySTEP 2022',
    applicationOpen: false,
    partner: 'mof',
    // externalLink: {
    //   link: 'https://mystep.talentlabs.org',
    //   linkTarget: '_blank'
    // },
    tagline: 'Earn While You Learn',
    subtext:
      'MySTEP, or Malaysia Short-term Employment Programme, is a Government initiative that provides short-term employment (contracts) opportunities in various fields such as technology and finance.',
    tracks: [
      {
        ...programTracks[1],
        courses: ['fcda', 'caba', 'cada'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Software Engineering',
        courses: ['fcse', 'cafed', 'cabed'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Digital Marketing',
        courses: ['fcdm'],
        description: '1 foundation course'
      },
      {
        title: 'Technology Management',
        courses: ['fif'],
        description: '1 foundation course'
      }
    ],
    sections: {
      hero: {
        image: '/images/database/programmes/mystep/hero.jpg',
        description: `<b>Get paid RM2,000 per month to build your career!</b><br/>
        Our 6-months structured apprenticeship programme prepares you for a career in technology, through intensive upskilling and internship at leading companies.`
      },
      overview: {
        image: '/images/database/programmes/mystep/overview.jpg',
        title: `Your Tech Career Starts <u>Here</u>`,
        description: `To address youth unemployment, our goal is to shift youths' efforts from seeking jobs to creating marketable opportunities for themselves.
        <br/><br/>
        With that, the Ministry of Finance (MoF) introduced the Malaysia Short Term Employment Programme (MySTEP) and JaminKerja initiative under Belanjawan 2021. It aims to provide job opportunities in both the private sector and government bodies and agencies, focusing on areas of high demand in the industry.
        <br/><br/>
        Through our vertical career platform, we are committed to advance your career goals and provide you with a proven path to a successful career in technology.`,
        logos: [
          '/images/database/employers/employer-khazanah.png',
          '/images/database/employers/employer-tl.png'
        ]
      },
      cards: {
        title: `What does TalentLabs <u>offer you?</u>`,
        cards: [
          {
            title: `Structured Learning Environment`,
            description: `You will go through a 6-months structured talent development programme combining a globally recognised certificate and on-the job training with TalentLabs' reputable hiring partners.`,
            color: tlColor.green,
            size: 'third',
            icon: '/images/database/programmes/kydp-25/card-icon-book.svg'
          },
          {
            title: `Professional Networking Opportunities`,
            description: `Develop your career and discover great opportunities by being part of TalentLabs extensive network of students, mentors and hiring partners based across the globe, from Canada to Hong Kong to Malaysia.`,
            color: tlColor.blue,
            dark: true,
            size: 'third',
            icon: '/images/database/programmes/gcc-25/card-icon-handshake.svg',
            image: '/images/database/programmes/kydp-25/card-0.jpg'
          },
          {
            title: `Career Management & Employability Support`,
            description: `We help you navigate the complex job application process while matching you with the most suitable career path in technology. From CV reviews to technical interview preparations, we've got you covered.`,
            color: tlColor.yellow,
            size: 'third',
            icon: '/images/database/programmes/gcc-25/card-icon-briefcase-2.svg'
          }
        ]
      },
      requirements: {
        title: `Who are we <u>looking for?</u>`,
        requirements: [
          'Recently unemployed or fresh graduates from IPTA, IPTS and Foreign Universities with good academic performance. STEM background is preferred but not required.',
          'Outstanding interpersonal skills, results-driven, agile, positive attitude towards learning and "can-do attitude"',
          'Strong computational thinking, problem solving and analytical skills with keen interest in the technology-related roles and industry'
        ]
      },
      timeline: {
        title: `How it <u>works</u>`,
        timeline: [
          {
            title: 'Apply now!',
            description: `<ul>
            <li>Fill in our online application form <a>here</a></li>
            <li>Choose your preferred career path</li>
            <li>Upload relevant documents such as Malaysian NRIC, CV and Academic Transcript</li>
            </ul>`,
            icon: 'apply'
          },
          {
            title: `Online assessments & interviews`,
            description: `<ul>
            <li>Complete a technical assessment which assesses your ability to solve problems, analyze and use information, think critically, and learn new skills.</li>
            <li>Complete a one-to-one interview, which provides you with the opportunity to display the qualities that your CV can't and reassure us that you've thought carefully about your career choice!</li>
            </ul>`,
            icon: 'pen'
          },
          {
            title: `You're accepted!`,
            description: `<ul>
            <li>Congratulations! Now all's that left is your employment paperwork.</li>
            <li>This set of agreement details important policies as well as your start and programme graduation dates. Make sure to read it carefully and ensure all the information you've entered are accurate.</li>
            </ul>`,
            icon: 'thumbs-up'
          },
          {
            title: `Online learning & Skills development`,
            description: `<ul>
            <li>2-months skills-based training from expert instructors & mentors</li>
            <li>Complete practical work assignments and projects to build your skills</li>
            <li> Extensive career management and employability support to help you transition into your new role</li>
            </ul>`,
            icon: 'cert'
          },
          {
            title: `Apprenticeship & On-the-Job Training`,
            description: `<ul>
            <li>4-months apprenticeship with TalentLabs hiring partners</li>
            <li>Get valuable, first-hand work experience at your designated company</li>
            <li>Put the skills you learned into practice!</li>
            </ul>`,
            icon: 'briefcase'
          }
        ]
      },
      courses: {
        title: `Choose your <u>career path</u>`,
        courses: [
          {
            id: 'fcda',
            description: `Data Analytics is the application of modern data analytics tools, data visualisation & optimisation to solve real-world business problems and help you understand the role of evidence-based data in decision making.`,
            jobs: [
              'Data Analyst',
              'Data Scientist',
              'Business Analyst',
              'Management Associate',
              'Operations Executive'
            ]
          },
          {
            id: 'fcse',
            description: `Software Engineering covers front-end and back-end web development, data structures, databases, and algorithms. The course touches on four key aspects of a software engineer: requirements, design, construction, and testing.`,
            jobs: [
              'Software Engineer',
              'Software Developer',
              'Web Developer',
              'Mobile App Developer',
              'Programmer'
            ]
          },
          {
            id: 'fctm',
            description: `Technology Management prepares you with the fundamental understanding, industry best practices & application concepts of the latest development in computer science and technology. Learn various cutting edge tech from blockchain to Al. and how these innovations can be applied in business & organisational settings.`,
            jobs: [
              'Business Development Executive',
              'Management Associate',
              'Consultant',
              'Operations Executive'
            ]
          },
          {
            id: 'fcdm',
            description: `Digital Marketing entails everything that happens on a digital platform, whether it's on a website, social media, or through email. You will learn to manage marketing campaigns by leveraging data on customers, products and interactions.`,
            jobs: ['Digital Marketer', 'Content Strategist', 'Brand Manager', 'Sales Executive']
          }
        ]
      },
      employers: {
        title: `Our <u>hiring companies</u>`,
        employers: [
          'fave',
          'samsung',
          'pwc',
          'proximax',
          'hl',
          'deloitte',
          'cimb',
          'lenovo',
          'khazanah',
          'maybank',
          'pos',
          'accenture',
          'nielsen'
        ]
      },
      prompt: {
        include: true,
        title: `Ready for big things?`,
        description: `Embark upon an exciting journey to transform your future`
      }
    }
  },
  {
    id: 'dtfs',
    title: 'Deep Tech & Future Skills Programme',
    shortTitle: 'Deep Tech',
    applicationOpen: false,
    partner: 'mosti',
    // externalLink: {
    //   link: 'https://mailchi.mp/9c1850d9fdd3/talentlabs-deep-tech-future-skills-22',
    //   linkTarget: '_blank'
    // },
    tagline: 'Join our fully-sponsored initiative today.',
    subtext:
      'Deeptech and Futureskills Upskilling and Reskilling is a strategic initiative by MOSTI under the National Employment Council (NEC).',
    tracks: [
      {
        ...programTracks[1],
        courses: ['fcda', 'caba', 'cada'],
        description: '1 foundation course, 2 associate courses'
      },
      {
        title: 'Software Engineering',
        courses: ['fcse', 'cafed', 'cabed'],
        description: '1 foundation course, 2 associate courses'
      }
    ],
    sections: {
      hero: {
        image: '/images/database/programmes/dfts/hero.jpg',
        description:
          'Let us help you take the lead in your organization with our 3-month acceleration program! <br/>Join our Deep Tech & Future Skills initiative today.'
      },
      overview: {
        title: 'Your Tech Career Upgrade Begins <u>Here</u>',
        description: `We're relentlessly focused on one thing: up-skilling talented future leaders.
        Our programme is carefully designed to guide you in selecting courses that meet your
        career needs. Instilling in-demand tech skills and providing extensive channels where candidates
        can spend their time to learn, grow, and build the right set of skills for a technology-driven career path. 
        <br/><br/>
        Our goal is to help you succeed in today's digital economy through the guidance of our industry experts.
        The Ministry of Science, Technology and Innovation (MoSTI) introduced the Deep Tech & Future Skills Programme initiative to up-skill graduates and create marketable job opportunities for themselves.
        Together with Yayasan Inovasi Malaysia, we are committed to advancing your career goals
        and providing you with a proven path to a successful career in technology.`
      },
      cards: {
        title: 'Built to Help You <u>Climb the Ladder</u> Faster',
        cards: [
          {
            title: 'Learn On Demand',
            description: `We aim to equip you with the necessary digital skills & knowledge required to stand out in the modern workplace, all at your own pace.`,
            size: 'third',
            color: tlColor.green,
            icon: '/images/database/programmes/kydp-25/card-icon-book.svg'
          },
          {
            title: 'Employability Support',
            description: `We offer personalised support and guidance on how to tackle the job market while matching you with suitable career specialisations.`,
            size: 'third',
            icon: '/images/database/programmes/gcc-25/card-icon-handshake.svg',
            image: '/images/database/programmes/kydp-25/card-0.jpg',
            dark: true
          },
          {
            title: 'Career Upgrade',
            description: `Remain relevant and competitive in the workforce through upskilling, and keep yourself updated with all the in-demand digital tech skills and trend.`,
            size: 'third',
            color: tlColor.yellow,
            icon: '/images/database/programmes/gcc-25/card-icon-briefcase-2.svg'
          }
        ]
      },
      courses: {
        title: `Our <u>intermediate courses</u> offerings`,
        courses: [
          {
            id: 'cada',
            description: `With an understanding of the business challenges of an enterprise, graduates will be trained in the latest Data Analytics methods and tools; including fundamental and advanced statistical and mathematical principles (machine learning, pattern recognition, data mining, etc.).`
          },
          {
            id: 'caba',
            description: `Business Analytics equips students to make data-driven and evidence-based decisions to drive transformation within businesses. Using business analytics tools and techniques, gain the ability to transform data into predictive insights to respond to the global Big Data Revolution.`
          },
          {
            id: 'cafed',
            description: `Get the skills to build engaging, interactive user experiences on the web as a Front-End Developer. Learn everything needed to design and develop user-facing code, and discover how to bring concepts to the browser by combining essential coding principles.`
          },
          {
            id: 'cabed',
            description: `Learn how to handle the 'behind-the-scenes' functionality of web applications. Back-End Development teaches the code that connects the web to a database, manages user connections, and powers the web application itself to deliver the final product to the end user.`
          }
        ]
      },
      testimony: {
        title: 'Our <u>graduates</u> say it best',
        students: topStudents
      },
      employers: {
        title: 'Our alumni have successfully secured placements at <u>top-tier companies</u>',
        employers: [
          'khazanah',
          'maybank',
          'lenovo',
          'kpmg',
          'petronas',
          'nielsen',
          'pwc',
          'bnm',
          'sunway',
          'intel',
          'pos',
          'keysight',
          'mbsb',
          'foodpanda',
          'deloitte',
          'daikin',
          'godaddy',
          'hl',
          'gamuda',
          'kwsp'
        ]
      },
      prompt: {
        include: true
      }
    }
  }
]
